import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Search, Star, X, Edit2, Archive, ArchiveRestore, Save, Plus, RotateCcw, Upload, CheckCircle2, AlertCircle, Loader2, Download, Check, Tag, Minus, MousePointer2, SlidersHorizontal, Link2, Wand2, UserRound, ChevronLeft, ChevronRight, BookOpen, Camera, Volume2, Send, ImagePlus, Layers } from 'lucide-react';
import MediaAIEditModal from '../components/MediaAIEditModal';
import WatermarkExportModal from '../components/WatermarkExportModal';
import SendMediaModal from '../components/SendMediaModal';
import { getCurrentUser } from '../services/auth';
import { neonQuery } from '../services/api';
import { fileToUploadOriginal, fileImageDims, isGifFile, GIF_RAW_MAX_BYTES } from '../lib/mediaUpload';
import { BRANDS, BRAND_VALUES, brandLabel, brandShort } from '../lib/brands';
import useIsMobile from '../hooks/useIsMobile';

const PAGE_SIZE = 200;
// Mirrors the classifier taxonomy in netlify/functions/media-library-upload-prompt.md.
// Kept in sync by hand: the prompt is the source of truth, this is the picker.
const CATEGORIES = ['products', 'plated', 'lifestyle', 'ugc', 'process', 'people', 'marketing', 'other'];
const PRODUCT_SUBS = ['hard-crabs', 'soft-crabs', 'crab-meat', 'crab-cakes', 'shrimp', 'fish', 'oysters-clams-mussels', 'lobster', 'pantry', 'other-seafood'];
const SUBCATEGORIES = {
  products: PRODUCT_SUBS,
  // Plated reuses the product list plus 'combo' when several proteins share top billing.
  plated: [...PRODUCT_SUBS, 'combo'],
  lifestyle: ['setting-outdoor', 'setting-indoor', 'setting-tablescape', 'mood'],
  ugc: ['at-home', 'at-event', 'with-pets-kids', 'unboxing'],
  process: ['steaming', 'packing', 'picking', 'dock-receiving', 'shipping'],
  people: ['staff', 'founders', 'watermen', 'mascot'],
  marketing: ['logo', 'banner', 'social-graphic', 'print', 'holiday'],
  other: [],
};
const USABLE_FOR = ['hero', 'thumb', 'social', 'email', 'print'];
const QUALITY_LEVELS = [0, 2, 3, 4, 5];
const ORIGINAL_MAX_EDGE = 2048;
const THUMB_MAX_EDGE = 480;
const ORIGINAL_QUALITY = 0.88;
const THUMB_QUALITY = 0.78;

async function fileToResizedBase64(file, maxEdge, quality) {
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('Could not read file'));
    fr.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Could not decode image (HEIC and RAW formats are not supported — convert to JPEG/PNG first)'));
    el.src = dataUrl;
  });
  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  const longEdge = Math.max(naturalWidth, naturalHeight);
  const scale = Math.min(1, maxEdge / longEdge);
  const w = Math.max(1, Math.round(naturalWidth * scale));
  const h = Math.max(1, Math.round(naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  const jpeg = canvas.toDataURL('image/jpeg', quality);
  const comma = jpeg.indexOf(',');
  return { base64: jpeg.slice(comma + 1), naturalWidth, naturalHeight };
}

const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|ogg)$/i;
const HEIC_EXT_RE = /\.(heic|heif)$/i;
const isVideoFile = (f) => VIDEO_EXT_RE.test(f.name) || /^video\//.test(f.type || '');
const isHeicFile = (f) => HEIC_EXT_RE.test(f.name) || /^image\/hei[cf]$/.test(f.type || '');

// Read a file as raw base64 (no canvas) — used for HEIC, which the browser
// can't decode but the server converts to JPEG.
async function fileToRawBase64(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('Could not read file'));
    fr.readAsDataURL(file);
  });
  return String(dataUrl).slice(String(dataUrl).indexOf(',') + 1);
}

async function sha256Hex(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Capture a poster frame from a video file (for the thumbnail + classification)
// and read its duration/dimensions. Done entirely in the browser.
async function videoPoster(file) {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.muted = true; video.playsInline = true; video.preload = 'metadata'; video.src = url;
    await new Promise((resolve, reject) => {
      video.onloadeddata = resolve;
      video.onerror = () => reject(new Error('Could not read video (unsupported codec — try MP4/H.264)'));
      setTimeout(() => reject(new Error('Timed out reading video')), 12000);
    });
    // Seek a little past the start so we don't grab a black opening frame.
    const target = Math.min(1, (video.duration || 2) / 3);
    await new Promise((resolve) => { video.onseeked = resolve; try { video.currentTime = target; } catch { resolve(); } });
    const w = video.videoWidth, h = video.videoHeight;
    const canvas = document.createElement('canvas');
    canvas.width = w || 640; canvas.height = h || 360;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    const jpeg = canvas.toDataURL('image/jpeg', 0.82);
    return {
      posterBase64: jpeg.slice(jpeg.indexOf(',') + 1),
      duration: isFinite(video.duration) ? Math.round(video.duration) : null,
      width: w || null, height: h || null,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function thumbUrl(key) {
  if (!key) return '';
  // r2-image is a tiny netlify function that proxies the bytes (private bucket).
  return `/.netlify/functions/r2-image?key=${encodeURIComponent(key)}`;
}

// A GIF asset = its stored original is a .gif. Grid thumbnails are always static
// JPEGs, so GIFs can't animate in tiles — they get a GIF badge in the grid and
// animate in the detail view (which renders the original file).
const isGifAsset = (item) => /\.gif$/i.test(item?.r2_original_key || '');

// Transparent assets (brand logos on a see-through background) carry a real
// alpha-preserving PNG thumbnail; render it over a checkerboard so "transparent"
// is visible instead of flattening onto black or vanishing on white.
const isTransparentAsset = (item) => item?.has_transparency === true;
const CHECKERBOARD_STYLE = {
  backgroundImage: 'conic-gradient(#e4e6ea 0 25%, #fafbfc 0 50%, #e4e6ea 0 75%, #fafbfc 0)',
  backgroundSize: '18px 18px',
};

export default function MediaLibrary() {
  const user = getCurrentUser();
  const userRoles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map((r) => r.trim());
  const isAllowed = userRoles.includes('Admin') || userRoles.includes('Manager') || userRoles.includes('Marketing');
  const isMobile = useIsMobile();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Filters
  const [brand, setBrand] = useState('crab-place');       // default to Crab Place; null = both
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const [minQuality, setMinQuality] = useState(0);
  const [usableForFilter, setUsableForFilter] = useState([]);
  const [tagFilter, setTagFilter] = useState([]);
  const [starredOnly, setStarredOnly] = useState(false);
  const [archivedView, setArchivedView] = useState('active');  // 'active' | 'all' | 'only'
  const [mediaKind, setMediaKind] = useState(null);             // null = both, 'image' | 'video'
  const [orientation, setOrientation] = useState(null);         // null = any, 'landscape' | 'portrait' | 'square'
  const [logoFilter, setLogoFilter] = useState(null);           // null = any, 'new-logo' | 'old-logo'
  const [transparentOnly, setTransparentOnly] = useState(false); // true = only files with real transparency (logos)
  // Photos that arrived as HEIC are converted to JPEG on upload and stored as
  // JPEG, so `source-heic` is provenance, not a format the grid can't render.
  // They used to be hidden by default, which silently swallowed most of a
  // batch when someone uploaded straight off a Mac or iPhone (31 of Rebecca's
  // 35 swordfish photos, 2026-09-01). Default is now VISIBLE.
  // NOTE the key rename: the old effect wrote 'true' on first render for every
  // user, so flipping the default alone would have changed nothing for anyone.
  const [hideHeic, setHideHeic] = useState(() => localStorage.getItem('cpx-media-hide-heic-v2') === 'true');
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState(() => localStorage.getItem('cpx-media-order-by') || 'quality');
  // null = any uploader, 'unknown' = legacy/unattributed, otherwise admin_id (number).
  const [uploaderFilter, setUploaderFilter] = useState(null);

  useEffect(() => {
    localStorage.setItem('cpx-media-order-by', orderBy);
  }, [orderBy]);

  useEffect(() => {
    localStorage.setItem('cpx-media-hide-heic-v2', String(hideHeic));
  }, [hideHeic]);

  // Data
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Facets
  const [brandCounts, setBrandCounts] = useState({});
  const [categoryCounts, setCategoryCounts] = useState([]);
  const [subcategoryCounts, setSubcategoryCounts] = useState([]);
  const [tagCounts, setTagCounts] = useState([]);
  const [tagPresets, setTagPresets] = useState([]);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const loadTagPresets = useCallback(async () => {
    try { const rows = await neonQuery('media_tag_presets_list', {}); setTagPresets(Array.isArray(rows) ? rows : []); }
    catch (_) { /* presets are a convenience; the library works without them */ }
  }, []);
  useEffect(() => { loadTagPresets(); }, [loadTagPresets]);
  const [tagSearch, setTagSearch] = useState('');
  const [uploaderCounts, setUploaderCounts] = useState([]);  // [{admin_id, name, count}]

  // Detail modal
  const [openItem, setOpenItem] = useState(null);
  const [users, setUsers] = useState([]); // adm_users for the "uploaded by" picker
  useEffect(() => {
    neonQuery('users_list').then((rows) => {
      setUsers((rows || []).filter((u) => u.staff_name && !u.deleted_at).map((u) => ({ admin_id: u.admin_id, staff_name: u.staff_name })));
    }).catch(() => {});
  }, []);
  const [aiEditFor, setAiEditFor] = useState(null);

  // ── Vernacular ─────────────────────────────────────────────────────────────
  // Does what was just typed mean something specific to us? The motivating case
  // is "official logo": those words appear in a thousand photos that merely have
  // a logo in frame, while the handful of actual logo FILES are buried. The
  // server already floats the curated set to the top; this chip makes that
  // visible and offers a one-click filter down to just them.
  // Terms are curated in Marketing > Media Intake > Vernacular.
  const [vocabHits, setVocabHits] = useState([]);
  const [glossary, setGlossary] = useState(null);   // null = drawer closed
  useEffect(() => {
    if (!search.trim()) { setVocabHits([]); return undefined; }
    let cancelled = false;
    // Debounced: the list query fires on every keystroke already, and this only
    // changes a hint chip — no need to race it.
    const t = setTimeout(() => {
      neonQuery('media_vocab_resolve', { search })
        // A term nobody has tagged anything with yet has nothing to offer here —
        // a chip reading "Official logo 0" is worse than no chip. It starts
        // earning its place the moment a batch or sweep applies it.
        .then((rows) => { if (!cancelled) setVocabHits((rows || []).filter((r) => r.asset_count > 0)); })
        .catch(() => { if (!cancelled) setVocabHits([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search]);

  const openGlossary = useCallback(async () => {
    setGlossary([]);
    try { setGlossary(await neonQuery('media_vocab_list', {}) || []); }
    catch (e) { setGlossary([]); }
  }, []);

  // Bumped by the ↻ reload button — refetches facets too, not just the list
  const [reloadTick, setReloadTick] = useState(0);

  // Upload state
  const [uploadQueue, setUploadQueue] = useState([]);   // [{ id, name, status, message?, row? }]
  // Deliberately null, not 'crab-place'. A preselected brand is the one field
  // nobody notices is wrong until the asset is filed under the wrong company
  // (Jess). Uploading is gated until brand, category and name are all set.
  const [uploadBrand, setUploadBrand] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadSubcategory, setUploadSubcategory] = useState('');
  const [uploadName, setUploadName] = useState('');
  const [uploadGateMsg, setUploadGateMsg] = useState(null);
  const [uploadPanelOpen, setUploadPanelOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // Pre-supplied tags applied to every file in the next upload batch. Sticky
  // across batches — admin clears manually when switching subject matter.
  const [uploadTags, setUploadTags] = useState([]);
  // The raw text in the "add a tag" box, lifted to the parent so the upload
  // routine can fold in a typed-but-not-yet-added tag at the moment files are
  // picked/dropped — no reliance on the user pressing Enter or the field blurring.
  const [uploadTagInput, setUploadTagInput] = useState('');
  const normalizeTag = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  // IDs of the assets created by the most-recent drop/pick, so a tag added in
  // the panel AFTER the files were dropped still lands on them (people often
  // drop first, then type the tag — the upload has already started by then).
  const [lastBatchIds, setLastBatchIds] = useState([]);
  // Live mirror of the committed pre-set tags, read at the END of a batch so a
  // tag typed while files were still uploading lands on every file in the batch
  // (not just the ones that had finished when the tag was committed).
  const uploadTagsRef = useRef([]);
  useEffect(() => { uploadTagsRef.current = uploadTags; }, [uploadTags]);
  // Read once at the START of each batch rather than through the render
  // closure, so a value set moments before the drop is never missed. Snapshotted
  // per batch on purpose: the numbering is derived from the base name, so a name
  // edited mid-upload must not renumber the files already sent.
  const uploadMetaRef = useRef({ brand: null, category: '', subcategory: '', name: '' });
  useEffect(() => {
    uploadMetaRef.current = { brand: uploadBrand, category: uploadCategory, subcategory: uploadSubcategory, name: uploadName };
  }, [uploadBrand, uploadCategory, uploadSubcategory, uploadName]);
  // Everything the upload panel requires before a file may be sent.
  const uploadReady = !!uploadBrand && !!uploadCategory && !!uploadName.trim();
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const contentScrollRef = useRef(null);  // the scrolling grid area; scroll to top when opening the upload panel

  // Open the upload panel AND scroll the content area back to the top so the
  // panel (which renders above the grid) is actually visible — otherwise, when
  // scrolled down, clicking Upload opens it off-screen with no feedback.
  const openUploadPanel = () => {
    setUploadPanelOpen(true);
    contentScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Bulk-selection state. selectedIds persists across filter changes (a hidden
  // selected tile stays selected — file-manager convention).
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const lastSelectedIdxRef = useRef(null);
  const [bulkPopover, setBulkPopover] = useState(null);   // 'add' | 'remove' | null
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMessage, setBulkMessage] = useState(null);   // { kind: 'ok'|'err', text }
  const [sendItems, setSendItems] = useState(null);        // items in the Send (email/text) dialog

  // ── Fetching ───────────────────────────────────────────────────────────────
  // Monotonic fetch sequence. Search fires a fetch per keystroke with no
  // debounce; a slower earlier query ("hard crabs m" — huge prefix match set)
  // can resolve AFTER a newer one and clobber the grid with stale results
  // (symptom: results don't update until one more keystroke, e.g. a trailing
  // space, fires an uncontested fetch). Responses from superseded fetches are
  // dropped.
  const fetchSeqRef = useRef(0);
  const fetchPage = useCallback(async (resetList = true) => {
    const seq = ++fetchSeqRef.current;
    setLoading(true); setError(null);
    try {
      const result = await neonQuery('media_assets_list', {
        search,
        brand,
        category,
        subcategory: category ? subcategory : null,
        minQuality,
        usableForAny: usableForFilter,
        tagsAll: logoFilter ? [...tagFilter, logoFilter] : tagFilter,
        excludeTags: hideHeic ? ['source-heic'] : [],
        starredOnly,
        includeArchived: archivedView === 'all',
        archivedOnly: archivedView === 'only',
        mediaKind,
        uploadedByAdminId: uploaderFilter,
        orientation,
        transparentOnly,
        orderBy,
        limit: PAGE_SIZE,
        offset: resetList ? 0 : offset,
      });
      // neonQuery returns rows from data.rows; total comes back on the result
      // but the wrapper drops it. Refetch total via a count field embedded in
      // each row (added in the SQL window). Fallback: rely on server total.
      // Server sends { rows, total } but neonQuery only returns rows. So we
      // pull total off the first row's _meta if present, else default to length.
      if (seq !== fetchSeqRef.current) return;  // a newer fetch superseded this one
      const rows = result || [];
      if (resetList) { setItems(rows); setOffset(rows.length); }
      else { setItems((prev) => [...prev, ...rows]); setOffset((o) => o + rows.length); }
      // The server returns a 'total' on the response envelope but neonQuery
      // discards it. For a true count we'd need a separate query — for now
      // approximate from the page size + offset.
      setTotal(rows.length < PAGE_SIZE ? (resetList ? rows.length : offset + rows.length) : null);
    } catch (e) { if (seq === fetchSeqRef.current) setError(e.message); }
    finally { if (seq === fetchSeqRef.current) setLoading(false); }
  }, [search, brand, category, subcategory, minQuality, usableForFilter, tagFilter, hideHeic, starredOnly, archivedView, mediaKind, uploaderFilter, orientation, transparentOnly, logoFilter, orderBy, offset]);

  // Reset list when filters change
  useEffect(() => {
    if (!isAllowed) return;
    fetchPage(true);
    // A new search/filter is a fresh result set — anchor back at the top so you
    // aren't left scrolled 20 rows down where the previous results had you.
    contentScrollRef.current?.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed, brand, category, subcategory, minQuality, usableForFilter, tagFilter, hideHeic, starredOnly, archivedView, mediaKind, uploaderFilter, orientation, transparentOnly, logoFilter, orderBy, search]);

  // Facet fetches — re-run when brand changes OR when reload is clicked
  useEffect(() => {
    if (!isAllowed) return;
    neonQuery('media_assets_brand_counts').then((rows) => {
      const map = {}; for (const r of rows) map[r.brand] = r.count; setBrandCounts(map);
    }).catch((e) => console.warn('brand_counts:', e.message));
    neonQuery('media_assets_category_counts', { brand }).then(setCategoryCounts).catch((e) => console.warn('category_counts:', e.message));
    // Scoped exactly like the grid — a chip that promises 27 must open 27.
    neonQuery('media_assets_tag_facets', {
      brand,
      includeArchived: archivedView === 'all',
      archivedOnly: archivedView === 'only',
      excludeTags: hideHeic ? ['source-heic'] : [],
    }).then(setTagCounts).catch((e) => console.warn('tag_facets:', e.message));
    neonQuery('media_assets_uploader_counts').then(setUploaderCounts).catch((e) => console.warn('uploader_counts:', e.message));
  }, [isAllowed, brand, archivedView, hideHeic, reloadTick]);

  // Subcategory facets when a category is selected
  useEffect(() => {
    if (!category) { setSubcategoryCounts([]); setSubcategory(null); return; }
    neonQuery('media_assets_subcategory_counts', { brand, category }).then(setSubcategoryCounts).catch((e) => console.warn('subcategory_counts:', e.message));
  }, [brand, category, reloadTick]);

  // ── Mutations ──────────────────────────────────────────────────────────────
  const toggleStar = async (item) => {
    const next = !item.human_starred;
    setItems((prev) => prev.map((m) => m.id === item.id ? { ...m, human_starred: next } : m));
    if (openItem?.id === item.id) setOpenItem({ ...openItem, human_starred: next });
    try { await neonQuery('media_assets_star_toggle', { id: item.id, admin_id: user?.id, starred: next }); }
    catch (e) { setError(`Star failed: ${e.message}`); }
  };

  const toggleArchive = async (item) => {
    const archive = !item.archived_at;
    try {
      const rows = await neonQuery('media_assets_archive_toggle', { id: item.id, admin_id: user?.id, archived: archive });
      const newAt = rows?.[0]?.archived_at || null;
      setItems((prev) => {
        // Drop from the visible list when the toggled state no longer matches
        // the current filter, otherwise update in place so the tile reflects
        // the new archived chip without disappearing on 'all' / matching views.
        if (archive && archivedView === 'active') return prev.filter((m) => m.id !== item.id);
        if (!archive && archivedView === 'only') return prev.filter((m) => m.id !== item.id);
        return prev.map((m) => m.id === item.id ? { ...m, archived_at: newAt } : m);
      });
      setOpenItem(null);
    } catch (e) { setError(`Archive failed: ${e.message}`); }
  };

  const saveEdit = async (id, fields) => {
    try {
      const rows = await neonQuery('media_assets_update', { id, admin_id: user?.id, fields });
      const updated = rows?.[0];
      if (updated) {
        // RETURNING doesn't include the joined uploader name — derive it locally.
        if ('uploaded_by_admin_id' in fields) {
          const u = users.find((x) => Number(x.admin_id) === Number(fields.uploaded_by_admin_id));
          updated.uploaded_by_name = fields.uploaded_by_admin_id ? (u ? u.staff_name : null) : null;
        }
        setItems((prev) => prev.map((m) => m.id === id ? { ...m, ...updated } : m));
        // RETURNING in media_assets_update is partial (only edited cols + audit) —
        // merge so r2_thumb_key, original_name, size_bytes, etc. survive in the open modal.
        setOpenItem((prev) => (prev && prev.id === id ? { ...prev, ...updated } : updated));
      }
    } catch (e) { setError(`Save failed: ${e.message}`); throw e; }
  };

  // ── Prev/next navigation inside the detail modal ───────────────────────────
  // Flip through the grid's current order without closing the modal. When the
  // open item nears the end of what's loaded, quietly fetch the next page so
  // "next" keeps working across the whole result set.
  const openIdx = openItem ? items.findIndex((m) => m.id === openItem.id) : -1;
  const openPrev = openIdx > 0 ? () => setOpenItem(items[openIdx - 1]) : null;
  const openNext = openIdx >= 0 && openIdx < items.length - 1 ? () => setOpenItem(items[openIdx + 1]) : null;
  useEffect(() => {
    if (openIdx >= 0 && openIdx >= items.length - 5 && items.length < total && !loading) fetchPage(false);
  }, [openIdx, items.length, total, loading, fetchPage]);

  // ── Bulk selection / tagging ───────────────────────────────────────────────
  // Toggle a single tile, OR shift-click → range select between last and current.
  const toggleSelect = useCallback((item, idx, shiftKey) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (shiftKey && lastSelectedIdxRef.current !== null) {
        const [a, b] = [lastSelectedIdxRef.current, idx].sort((x, y) => x - y);
        // Match the last-clicked tile's resulting state — if it ended up selected,
        // shift-range adds; if unselected, shift-range removes.
        const adding = next.has(item.id) ? false : true;
        for (let i = a; i <= b; i++) {
          const m = items[i];
          if (!m) continue;
          if (adding) next.add(m.id); else next.delete(m.id);
        }
      } else {
        if (next.has(item.id)) next.delete(item.id);
        else next.add(item.id);
      }
      lastSelectedIdxRef.current = idx;
      return next;
    });
  }, [items]);

  const exitSelection = () => { setSelectionMode(false); setSelectedIds(new Set()); setBulkPopover(null); lastSelectedIdxRef.current = null; };
  const clearSelection = () => { setSelectedIds(new Set()); lastSelectedIdxRef.current = null; };

  const runBulkTag = async ({ addTags = [], removeTags = [] }) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true); setBulkMessage(null);
    try {
      const result = await neonQuery('media_assets_bulk_tag', {
        ids, addTags, removeTags, admin_id: user?.id,
      });
      // result === rows array of { id, tags }
      const tagsById = new Map((result || []).map((r) => [r.id, r.tags || []]));
      setItems((prev) => prev.map((m) => tagsById.has(m.id) ? { ...m, tags: tagsById.get(m.id) } : m));
      setReloadTick((t) => t + 1); // refresh tag facets so new tag chips show in sidebar
      // Refetch the grid so items that no longer match the active filter
      // (e.g. just removed a tag the user is filtering on) drop out of view.
      fetchPage(true);
      const verb = addTags.length ? `added "${addTags.join(', ')}"` : `removed "${removeTags.join(', ')}"`;
      setBulkMessage({ kind: 'ok', text: `${verb} on ${ids.length} image${ids.length === 1 ? '' : 's'}` });
      setBulkPopover(null);
      setTimeout(() => setBulkMessage(null), 3500);
    } catch (e) {
      setBulkMessage({ kind: 'err', text: `Bulk tag failed: ${e.message}` });
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkSetBrand = async (newBrand) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true); setBulkMessage(null);
    try {
      const result = await neonQuery('media_assets_bulk_set_brand', {
        ids, brand: newBrand, admin_id: user?.id,
      });
      const brandById = new Map((result || []).map((r) => [r.id, r.brand]));
      setItems((prev) => prev.map((m) => brandById.has(m.id) ? { ...m, brand: brandById.get(m.id) } : m));
      setReloadTick((t) => t + 1); // brand counts in sidebar should refresh
      // Refetch the grid so items whose new brand no longer matches the
      // active brand filter (e.g. user is filtering Crab Place and just
      // flipped 24 to C&C) drop out of view.
      fetchPage(true);
      const lbl = brandLabel(newBrand);
      setBulkMessage({ kind: 'ok', text: `set brand to "${lbl}" on ${ids.length} image${ids.length === 1 ? '' : 's'}` });
      setBulkPopover(null);
      setTimeout(() => setBulkMessage(null), 3500);
    } catch (e) {
      setBulkMessage({ kind: 'err', text: `Bulk brand failed: ${e.message}` });
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkSetUploader = async (uid, onlyIfBlank) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true); setBulkMessage(null);
    try {
      const result = await neonQuery('media_assets_bulk_set_uploader', {
        ids, uploaded_by_admin_id: uid, onlyIfBlank: !!onlyIfBlank, admin_id: user?.id,
      });
      const u = users.find((x) => Number(x.admin_id) === Number(uid));
      const name = uid ? (u ? u.staff_name : null) : null;
      const byId = new Map((result || []).map((r) => [r.id, r.uploaded_by_admin_id]));
      setItems((prev) => prev.map((m) => byId.has(m.id) ? { ...m, uploaded_by_admin_id: byId.get(m.id), uploaded_by_name: byId.get(m.id) ? name : null } : m));
      setReloadTick((t) => t + 1); // uploader counts in the sidebar should refresh
      fetchPage(true); // items may drop out if filtering by "Uploaded by"
      setBulkMessage({ kind: 'ok', text: `set uploader to "${name || 'none'}" on ${(result || []).length} image${(result || []).length === 1 ? '' : 's'}${onlyIfBlank ? ' (blanks only)' : ''}` });
      setBulkPopover(null);
      setTimeout(() => setBulkMessage(null), 3500);
    } catch (e) {
      setBulkMessage({ kind: 'err', text: `Bulk uploader failed: ${e.message}` });
    } finally {
      setBulkBusy(false);
    }
  };

  // Tags currently present on at least one selected item — shown in the
  // "Remove tag" popover so you can pick from what's actually applied.
  const tagsOnSelection = useMemo(() => {
    if (!selectedIds.size) return [];
    const counts = new Map();
    for (const m of items) {
      if (!selectedIds.has(m.id)) continue;
      for (const t of (m.tags || [])) counts.set(t, (counts.get(t) || 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([tag, count]) => ({ tag, count }));
  }, [items, selectedIds]);

  // Per-brand counts within the selection — drives the "Set brand" popover so
  // it shows "(N already)" next to each option and disables the Apply button
  // when the new brand matches what's already on every selected image.
  const brandsOnSelection = useMemo(() => {
    const counts = { other: 0 };
    for (const v of BRAND_VALUES) counts[v] = 0;
    if (!selectedIds.size) return counts;
    for (const m of items) {
      if (!selectedIds.has(m.id)) continue;
      const b = m.brand;
      if (BRAND_VALUES.includes(b)) counts[b] += 1;
      else counts.other += 1;
    }
    return counts;
  }, [items, selectedIds]);

  // ── Uploads ────────────────────────────────────────────────────────────────
  // Add one or more tags to a set of already-saved assets and reflect the
  // result in the grid. Shared by the end-of-batch catch-all and the
  // tag-added-after-upload path.
  const applyTagsToIds = useCallback(async (ids, rawTags) => {
    const addTags = Array.from(new Set((rawTags || []).map(normalizeTag).filter(Boolean)));
    if (!ids?.length || !addTags.length) return;
    try {
      const result = await neonQuery('media_assets_bulk_tag', {
        ids, addTags, removeTags: [], admin_id: user?.id,
      });
      const tagsById = new Map((result || []).map((r) => [r.id, r.tags || []]));
      setItems((prev) => prev.map((m) => tagsById.has(m.id) ? { ...m, tags: tagsById.get(m.id) } : m));
      setReloadTick((tick) => tick + 1);
    } catch (e) {
      console.warn('apply tags failed:', e.message);
    }
  }, [user?.id]);

  // Processes files sequentially so Anthropic classify doesn't get rate-limited
  // and so giant multi-file drops don't blow up memory at once.
  const runUploads = useCallback(async (fileList) => {
    // Folder picks (webkitdirectory) hand back every descendant file. Filter
    // down to media extensions here so we don't spew "Unsupported type" rows
    // for stray .DS_Store / .json / .txt siblings the OS dropped in.
    const MEDIA_EXT = /\.(jpe?g|png|webp|gif|heic|heif|mp4|mov|m4v|webm|ogg)$/i;
    const files = Array.from(fileList || []).filter((f) => {
      if (!f || f.size === 0) return false;
      // A media extension OR a real image/video MIME type is in.
      return MEDIA_EXT.test(f.name) || /^image\//.test(f.type || '') || /^video\//.test(f.type || '');
    });
    if (!files.length) return;
    setUploadPanelOpen(true);

    // Required fields, checked here rather than only on the buttons so a
    // drag-and-drop can't slip past the gate. Read from the ref so a value the
    // user set moments ago is never missed by a stale closure.
    const meta = uploadMetaRef.current;
    const missing = [
      !meta.brand && 'brand',
      !meta.category && 'category',
      !String(meta.name || '').trim() && 'name',
    ].filter(Boolean);
    if (missing.length) {
      setUploadGateMsg(`Set ${missing.join(', ')} before uploading — these files were not uploaded.`);
      return;
    }
    setUploadGateMsg(null);

    // One name for the batch. A single file takes it as-is; two or more get a
    // sequence ("crab feast" -> "crab feast 1", "crab feast 2", ...) so the set
    // stays in order and no two files land under the same name. All files are
    // numbered rather than only the ones after the first — a lone unnumbered
    // "crab feast" sitting next to "crab feast 2" reads like a mistake.
    //
    // Numbering CONTINUES across batches (Jess, 2026-08-19): before naming, ask
    // the server for the highest sequence this name already has in the library,
    // and start after it — a second "crab feast" batch next week comes out
    // 4, 5, 6 rather than a duplicate "crab feast 1". A single file only stays
    // un-numbered when the name is genuinely fresh; if earlier files exist it
    // takes the next number too. The lookup fails open to the old start-at-1
    // behavior so a hiccup can never block an upload.
    const baseName = String(meta.name).trim();
    let startSeq = 0;
    try {
      const seqRes = await fetch('/.netlify/functions/media-library-upload', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'name-seq', name: baseName }),
      });
      const seqData = await seqRes.json();
      if (seqRes.ok && Number.isFinite(seqData.maxSeq)) startSeq = seqData.maxSeq;
    } catch { /* fail open — number from 1 like before */ }
    const nameFor = (i) => (files.length > 1 || startSeq > 0 ? `${baseName} ${startSeq + i + 1}` : baseName);

    // Fold any typed-but-not-yet-added tag (text still sitting in the box) into
    // the batch so it's never lost — covers drag-drop and "Choose files" where
    // the field never blurred and Enter was never pressed. This is the source
    // of truth for what gets sent; committed chips + the pending text.
    const pending = normalizeTag(uploadTagInput);
    const effectiveTags = pending && !uploadTags.includes(pending) ? [...uploadTags, pending] : uploadTags;
    if (pending) { setUploadTags(effectiveTags); setUploadTagInput(''); }  // keep it sticky for the next batch
    setLastBatchIds([]);  // start a fresh "current batch" for retroactive tagging
    const batchIds = [];  // local, reliable list of IDs created by THIS batch

    // Snapshot every photo's bytes NOW, while the browser can still read it.
    // The loop below is sequential, so file 20 gets read minutes after the
    // pick - by then a Mac has often moved or rewritten it (photos dragged out
    // of Photos land in a temp folder that gets cleaned up; iCloud re-syncs
    // Desktop/Documents), and the browser refuses: "Could not read file".
    // Rebecca, Nov 3, 2026: 7 HEICs uploaded, the next 21 failed that way.
    // Videos are too big to hold in memory and keep the live File, as does any
    // batch over ~600 MB.
    const snapshotOk = files.reduce((n, f) => n + (isVideoFile(f) ? 0 : f.size), 0) <= 600 * 1024 * 1024;
    const snapshotted = await Promise.all(files.map(async (f) => {
      if (isVideoFile(f) || !snapshotOk) return { file: f };
      try {
        return { file: new File([await f.arrayBuffer()], f.name, { type: f.type, lastModified: f.lastModified }) };
      } catch {
        return { file: f, unreadable: true };
      }
    }));
    const newItems = files.map((f, i) => ({
      id: `up-${Date.now()}-${i}`,
      name: f.name,
      size: f.size,
      status: 'queued',
      file: snapshotted[i].file,
      unreadable: snapshotted[i].unreadable,
      assetName: nameFor(i),
    }));
    setUploadQueue((prev) => [...newItems, ...prev]);

    const UPLOAD_URL = '/.netlify/functions/media-library-upload';
    for (const item of newItems) {
      const setItem = (patch) => setUploadQueue((prev) => prev.map((x) => x.id === item.id ? { ...x, ...patch } : x));
      const kind = isVideoFile(item.file) ? 'video'
        : isHeicFile(item.file) ? 'heic'
        // GIFs upload as raw bytes so animation + transparency survive (the
        // canvas path flattens to one JPEG frame). Oversized ones fall through
        // to the image path — flattened, same as before.
        : (isGifFile(item.file) && item.file.size <= GIF_RAW_MAX_BYTES) ? 'gif'
        : (/^image\//.test(item.file.type) || /\.(jpe?g|png|webp|gif)$/i.test(item.name)) ? 'image'
        : 'unknown';
      if (kind === 'unknown') {
        setItem({ status: 'error', message: `Unsupported type: ${item.file.type || 'unknown'}` });
        continue;
      }
      if (item.unreadable) {
        setItem({ status: 'error', message: 'Your computer would not hand over this file. If it came from Photos or iCloud, export it to a folder first, then upload from there.' });
        continue;
      }
      // Shared "this asset saved" handling for every media kind.
      const accept = (data) => {
        if (data.row) {
          setItems((prev) => [data.row, ...prev.filter((m) => m.id !== data.row.id)]);
          setLastBatchIds((prev) => [...prev, data.row.id]);
          batchIds.push(data.row.id);
        }
        setItem({
          status: 'done',
          message: data.duplicate
            ? 'Already in library'
            : (data.classified === false
              ? `Uploaded (auto-classify failed — needs review)`
              : `${data.row.category}/${data.row.subcategory} · q${data.row.quality_score}`),
          row: data.row,
        });
      };
      try {
        if (kind === 'video') {
          // Large file → presigned direct-to-R2 PUT, then register from a poster frame.
          setItem({ status: 'resizing', message: 'reading video…' });
          const sha = await sha256Hex(item.file);
          // Capturing a poster frame requires the browser to DECODE the video.
          // Many camera/phone .MOV files use codecs the browser can't decode
          // (HEVC/H.265, ProRes) — that decode hangs or errors, but it must NOT
          // block the upload. Fall back to no poster; the server then stores a
          // placeholder thumbnail and classifies from the filename.
          let poster = { posterBase64: null, duration: null, width: null, height: null };
          try {
            // Bounded from the caller as well as inside videoPoster. This loop
            // is SEQUENTIAL, so anything that never settles here stops the whole
            // batch, not just this file - the failure Rebecca hit on 2026-09-09.
            poster = await Promise.race([
              videoPoster(item.file),
              new Promise((resolve) => setTimeout(() => resolve(poster), 20000)),
            ]);
          } catch {
            /* undecodable codec — upload the bytes anyway, no thumbnail frame */
          }
          const ext = (item.name.match(/\.([a-z0-9]+)$/i) || [, 'mp4'])[1].toLowerCase();
          const pres = await fetch(UPLOAD_URL, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'presign', sha, ext }),
          });
          const presData = await pres.json().catch(() => ({}));
          if (!pres.ok) throw new Error(presData.error || `presign HTTP ${pres.status}`);
          setItem({ status: 'uploading', message: 'uploading video…' });
          const put = await fetch(presData.uploadUrl, {
            method: 'PUT', headers: { 'Content-Type': presData.contentType }, body: item.file,
          });
          if (!put.ok) throw new Error(`R2 upload failed (HTTP ${put.status})`);
          const reg = await fetch(UPLOAD_URL, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'register-video', sha, key: presData.key, contentType: presData.contentType,
              filename: item.name, posterBase64: poster.posterBase64, duration: poster.duration,
              width: poster.width, height: poster.height, brand: meta.brand, admin_id: user?.id, extraTags: effectiveTags,
              overrideCategory: meta.category, overrideSubcategory: meta.subcategory, overrideName: item.assetName,
            }),
          });
          const regData = await reg.json().catch(() => ({}));
          if (!reg.ok) throw new Error(regData.error || `register HTTP ${reg.status}`);
          accept(regData);
        } else if (kind === 'gif') {
          // Raw bytes — no canvas step. The server thumbs from the first frame
          // (PNG thumb if the GIF is transparent) and classifies the real GIF.
          setItem({ status: 'resizing', message: 'reading GIF…' });
          const [raw, dims] = await Promise.all([fileToRawBase64(item.file), fileImageDims(item.file)]);
          setItem({ status: 'uploading' });
          const res = await fetch(UPLOAD_URL, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              filename: item.name, contentType: 'image/gif', brand: meta.brand, admin_id: user?.id,
              originalBase64: raw, width: dims.width, height: dims.height, extraTags: effectiveTags,
              overrideCategory: meta.category, overrideSubcategory: meta.subcategory, overrideName: item.assetName,
            }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
          accept(data);
        } else if (kind === 'heic') {
          // Browser can't decode HEIC — send the raw bytes; server converts to
          // JPEG, makes the thumb, and classifies.
          setItem({ status: 'resizing', message: 'reading HEIC…' });
          const raw = await fileToRawBase64(item.file);
          setItem({ status: 'uploading' });
          const res = await fetch(UPLOAD_URL, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              filename: item.name, contentType: 'image/heic', brand: meta.brand,
              admin_id: user?.id, originalBase64: raw, extraTags: effectiveTags,
              overrideCategory: meta.category, overrideSubcategory: meta.subcategory, overrideName: item.assetName,
            }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
          accept(data);
        } else {
          // Image — resize client-side and POST as base64. Alpha-aware: a
          // PNG/WebP with real transparency (brand logos) stays PNG so the
          // transparency survives; everything else ships as JPEG like before.
          // The server discards our JPEG thumb for transparent files and makes
          // its own PNG thumb.
          setItem({ status: 'resizing' });
          const [originalResult, thumbResult] = await Promise.all([
            fileToUploadOriginal(item.file, ORIGINAL_MAX_EDGE, ORIGINAL_QUALITY),
            fileToResizedBase64(item.file, THUMB_MAX_EDGE, THUMB_QUALITY),
          ]);
          const isPng = originalResult.format === 'png';
          setItem({ status: 'uploading' });
          const res = await fetch(UPLOAD_URL, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              // Keep the extension truthful: a transparent .webp re-encodes to PNG.
              filename: isPng ? item.name.replace(/\.[a-z0-9]+$/i, '.png') : item.name,
              contentType: isPng ? 'image/png' : 'image/jpeg', brand: meta.brand, admin_id: user?.id,
              originalBase64: originalResult.base64, thumbBase64: thumbResult.base64,
              width: originalResult.naturalWidth, height: originalResult.naturalHeight, extraTags: effectiveTags,
              overrideCategory: meta.category, overrideSubcategory: meta.subcategory, overrideName: item.assetName,
            }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
          accept(data);
        }
      } catch (e) {
        setItem({ status: 'error', message: e.message.slice(0, 180) });
      }
    }

    // Catch-all: once the whole batch is in, re-apply whatever tags are
    // committed in the panel NOW (read live via the ref) to every file in the
    // batch. This covers a tag typed while files were still uploading — the
    // per-file send and the tag-added handler can both miss in-flight files,
    // but this final sweep tags them all. No-op if no pre-set tags.
    await applyTagsToIds(batchIds, uploadTagsRef.current);
  }, [user?.id, uploadTags, uploadTagInput, applyTagsToIds]);

  const onFilesPicked = (e) => {
    runUploads(e.target.files);
    e.target.value = '';  // allow re-picking the same file
  };

  // Tag added in the panel AFTER a batch already finished uploading — apply it
  // to that batch. (Mid-upload tags are handled by the end-of-batch sweep.)
  const applyTagToLastBatch = useCallback((tag) => applyTagsToIds(lastBatchIds, [tag]), [applyTagsToIds, lastBatchIds]);

  const clearCompletedUploads = () => {
    setUploadQueue((prev) => prev.filter((x) => x.status !== 'done'));
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (!isAllowed) {
    return (
      <div style={{ padding: 24 }}>
        <h1 style={{ color: 'var(--navy)', fontWeight: 700 }}>Media Library</h1>
        <p style={{ color: 'var(--gray-500)' }}>You need Admin, Manager, or Marketing access.</p>
      </div>
    );
  }

  const anyFilter = brand || category || subcategory || minQuality || usableForFilter.length || tagFilter.length || starredOnly || search || archivedView !== 'active' || mediaKind || orientation || transparentOnly || logoFilter || uploaderFilter !== null || hideHeic;

  const sidebarStyle = isMobile
    ? {
        position: 'fixed', top: 0, left: 0, bottom: 0, width: 'min(300px, 86vw)',
        borderRight: '1px solid var(--gray-200)', background: '#fafafa',
        overflowY: 'auto', padding: '14px 14px 80px',
        zIndex: 1100,
        transform: filterDrawerOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.22s ease-out',
        boxShadow: filterDrawerOpen ? '4px 0 16px rgba(0,0,0,0.18)' : 'none',
      }
    : { width: 240, borderRight: '1px solid var(--gray-200)', background: '#fafafa', overflowY: 'auto', padding: '16px 14px', flexShrink: 0 };

  return (
    // ⚠️ The desktop height subtracts a 56px topbar. On a phone the topbar is
    // taller (it stacks) and an approvals/health banner may sit under it, so
    // that calc runs ~120px long and pushes the grid — and the bulk action bar
    // pinned to its bottom — below the fold. .page-content is already a bounded
    // flex child, so 100% is both correct and chrome-agnostic there.
    <div className="cpx-touch" style={{ display: 'flex', height: isMobile ? '100%' : 'calc(100vh - 56px)', minHeight: isMobile ? 0 : undefined, overflow: 'hidden', position: 'relative' }}>
      {/* Filter sidebar (drawer on mobile) */}
      {isMobile && filterDrawerOpen ? (
        <div onClick={() => setFilterDrawerOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1099 }} />
      ) : null}
      <aside style={sidebarStyle}>
        {isMobile ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--gray-200)' }}>
            <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>Filters</div>
            <button onClick={() => setFilterDrawerOpen(false)} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', display: 'inline-flex' }}>
              <X size={18} color="var(--gray-500)" />
            </button>
          </div>
        ) : null}
        {/* Phones have no room for the header Sort dropdown, so it lives at the top of this panel. */}
        {isMobile ? (
          <FilterGroup label="Sort">
            {[['newest', 'Newest first'], ['oldest', 'Oldest first'], ['quality', 'Quality'], ['name', 'Filename'], ['uploader', 'Uploaded by']].map(([v, l]) => (
              <FilterChip key={v} label={l} active={orderBy === v} onClick={() => setOrderBy(v)} />
            ))}
          </FilterGroup>
        ) : null}
        <FilterGroup label="Brand">
          <FilterChip label={`All (${BRAND_VALUES.reduce((n, v) => n + (brandCounts[v] || 0), 0)})`} active={!brand} onClick={() => setBrand(null)} />
          {BRANDS.map((b) => (
            <FilterChip key={b.value} label={`${b.label} (${brandCounts[b.value] || 0})`} active={brand === b.value} onClick={() => setBrand(b.value)} />
          ))}
        </FilterGroup>

        <FilterGroup label="Type">
          <FilterChip label="all" active={!mediaKind} onClick={() => setMediaKind(null)} />
          <FilterChip label="images" active={mediaKind === 'image'} onClick={() => setMediaKind('image')} />
          <FilterChip label="videos" active={mediaKind === 'video'} onClick={() => setMediaKind('video')} />
          <FilterChip label="gifs" active={mediaKind === 'gif'} onClick={() => setMediaKind('gif')} />
          <FilterChip label="audio" active={mediaKind === 'audio'} onClick={() => setMediaKind('audio')} />
        </FilterGroup>

        <FilterGroup label="Orientation">
          <FilterChip label="any" active={!orientation} onClick={() => setOrientation(null)} />
          <FilterChip label="landscape" active={orientation === 'landscape'} onClick={() => setOrientation(orientation === 'landscape' ? null : 'landscape')} />
          <FilterChip label="portrait" active={orientation === 'portrait'} onClick={() => setOrientation(orientation === 'portrait' ? null : 'portrait')} />
          <FilterChip label="square" active={orientation === 'square'} onClick={() => setOrientation(orientation === 'square' ? null : 'square')} />
        </FilterGroup>

        <FilterGroup label="Transparency">
          <FilterChip label="any" active={!transparentOnly} onClick={() => setTransparentOnly(false)} />
          <FilterChip label="transparent" active={transparentOnly} onClick={() => setTransparentOnly(!transparentOnly)} />
        </FilterGroup>

        <FilterGroup label="Logo">
          <FilterChip label="any" active={!logoFilter} onClick={() => setLogoFilter(null)} />
          <FilterChip label="new logo" active={logoFilter === 'new-logo'} onClick={() => setLogoFilter(logoFilter === 'new-logo' ? null : 'new-logo')} />
          <FilterChip label="old logo" active={logoFilter === 'old-logo'} onClick={() => setLogoFilter(logoFilter === 'old-logo' ? null : 'old-logo')} />
        </FilterGroup>

        <FilterGroup label="Uploaded by">
          <FilterChip label="anyone" active={uploaderFilter === null} onClick={() => setUploaderFilter(null)} />
          {uploaderCounts.map((u) => {
            const key = u.admin_id == null ? 'unknown' : u.admin_id;
            const active = uploaderFilter === key || (u.admin_id == null && uploaderFilter === 'unknown') || (u.admin_id != null && uploaderFilter === u.admin_id);
            return (
              <FilterChip
                key={String(key)}
                label={`${u.name} (${u.count})`}
                active={active}
                onClick={() => setUploaderFilter(active ? null : key)}
              />
            );
          })}
        </FilterGroup>

        <FilterGroup label="Category">
          <FilterChip label="All" active={!category} onClick={() => { setCategory(null); setSubcategory(null); }} />
          {categoryCounts.map((c) => (
            <FilterChip key={c.category} label={`${c.category} (${c.count})`} active={category === c.category}
              onClick={() => { setCategory(category === c.category ? null : c.category); setSubcategory(null); }} />
          ))}
        </FilterGroup>

        {category && subcategoryCounts.length > 0 ? (
          <FilterGroup label={`Subcategory (${category})`}>
            <FilterChip label="any" active={!subcategory} onClick={() => setSubcategory(null)} />
            {subcategoryCounts.map((s) => (
              <FilterChip key={s.subcategory} label={`${s.subcategory} (${s.count})`} active={subcategory === s.subcategory}
                onClick={() => setSubcategory(subcategory === s.subcategory ? null : s.subcategory)} />
            ))}
          </FilterGroup>
        ) : null}

        <FilterGroup label="Min quality">
          <div style={{ display: 'flex', gap: 4 }}>
            {QUALITY_LEVELS.map((q) => (
              <button key={q} onClick={() => setMinQuality(q)} style={qButtonStyle(minQuality === q)}>
                {q === 0 ? 'any' : `${q}+★`}
              </button>
            ))}
          </div>
        </FilterGroup>

        <FilterGroup label="Usable for">
          {USABLE_FOR.map((u) => (
            <FilterChip key={u} label={u} active={usableForFilter.includes(u)}
              onClick={() => setUsableForFilter((prev) => prev.includes(u) ? prev.filter((x) => x !== u) : [...prev, u])} />
          ))}
        </FilterGroup>

        <FilterGroup label="Starred">
          <FilterChip label={starredOnly ? 'starred only' : 'show all'} active={starredOnly} onClick={() => setStarredOnly(!starredOnly)} />
        </FilterGroup>

        <FilterGroup label="Archived">
          <FilterChip label="active only" active={archivedView === 'active'} onClick={() => setArchivedView('active')} />
          <FilterChip label="include archived" active={archivedView === 'all'} onClick={() => setArchivedView('all')} />
          <FilterChip label="archived only" active={archivedView === 'only'} onClick={() => setArchivedView('only')} />
        </FilterGroup>

        <FilterGroup label="Shot on iPhone / Mac (HEIC)">
          <FilterChip label={hideHeic ? 'hidden' : 'shown with everything else'} active={!hideHeic} onClick={() => setHideHeic(!hideHeic)} />
        </FilterGroup>

        {(() => {
          const q = tagSearch.trim().toLowerCase();
          const visibleTags = q ? tagCounts.filter((t) => t.tag.toLowerCase().includes(q)) : tagCounts;
          return (
            <FilterGroup label={`Tags (${tagCounts.length}${q ? ` · ${visibleTags.length} match` : ''})`}>
              <div style={{ position: 'relative', marginBottom: 6 }}>
                <Search size={11} style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
                <input type="text" value={tagSearch} onChange={(e) => setTagSearch(e.target.value)}
                  placeholder="Filter tags…"
                  style={{ width: '100%', padding: '4px 22px 4px 22px', border: '1px solid var(--gray-200)', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                {tagSearch ? (
                  <button onClick={() => setTagSearch('')} style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 1, display: 'inline-flex' }}>
                    <X size={11} color="var(--gray-500)" />
                  </button>
                ) : null}
              </div>
              <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {visibleTags.map((t) => (
                  <FilterChip key={t.tag} label={`${t.tag} (${t.count})`} active={tagFilter.includes(t.tag)}
                    onClick={() => setTagFilter((prev) => prev.includes(t.tag) ? prev.filter((x) => x !== t.tag) : [...prev, t.tag])} />
                ))}
                {q && visibleTags.length === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', padding: '4px 2px' }}>No tags match "{tagSearch}".</div>
                ) : null}
              </div>
            </FilterGroup>
          );
        })()}

        {anyFilter ? (
          <button onClick={() => { setBrand(null); setCategory(null); setSubcategory(null); setMinQuality(0); setUsableForFilter([]); setTagFilter([]); setTagSearch(''); setStarredOnly(false); setArchivedView('active'); setMediaKind(null); setOrientation(null); setTransparentOnly(false); setLogoFilter(null); setHideHeic(false); setSearch(''); setUploaderFilter(null); }}
            style={{ width: '100%', marginTop: 12, padding: '6px 10px', fontSize: 12, color: 'var(--orange)', background: 'transparent', border: '1px solid var(--orange)', borderRadius: 4, cursor: 'pointer' }}>
            Clear all filters
          </button>
        ) : null}
      </aside>

      {/* Main pane */}
      <main
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}
        onDragOver={(e) => { e.preventDefault(); if (!isDragging) setIsDragging(true); }}
        onDragLeave={(e) => {
          // only hide when leaving the outer bounds — otherwise moving between children flickers
          if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          const files = e.dataTransfer?.files;
          // Open the panel first so a drop that fails the required-field gate
          // lands somewhere the message is actually visible.
          if (files?.length) { setUploadPanelOpen(true); runUploads(files); }
        }}
      >
        <header style={{ padding: isMobile ? '10px 12px' : '14px 20px', borderBottom: '1px solid var(--gray-200)', background: '#fff', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', flexWrap: isMobile ? 'nowrap' : 'wrap', gap: isMobile ? 8 : '10px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: isMobile ? '100%' : 'auto', flexShrink: 0 }}>
            {isMobile ? (
              <button onClick={() => setFilterDrawerOpen(true)} title="Show filters"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 10px', fontSize: 12, fontWeight: 600, background: '#fff', color: '#1d1d1f', border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer', flexShrink: 0 }}>
                <SlidersHorizontal size={14} /> Filters
              </button>
            ) : null}
            <div style={{ flex: isMobile ? 1 : '0 0 auto', minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: isMobile ? 15 : 18, color: 'var(--navy)', fontWeight: 700 }}>Media Library</h1>
              <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                {loading ? 'loading…' : `${items.length}${total !== null ? ` of ${total}` : '+'} images`}
                <button onClick={() => { setReloadTick((t) => t + 1); fetchPage(true); }} title="Refresh list and counts" style={{ marginLeft: 8, padding: '2px 6px', fontSize: 11, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 4, cursor: 'pointer' }}>
                  <RotateCcw size={11} style={{ verticalAlign: 'middle' }} /> reload
                </button>
              </div>
            </div>
            {isMobile ? (
              <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', flexShrink: 0 }}>
                <button
                  onClick={() => { if (selectionMode) { exitSelection(); } else { setSelectionMode(true); } }}
                  title={selectionMode ? 'Exit selection mode' : 'Select multiple'}
                  style={{ display: 'inline-flex', alignItems: 'center', padding: 8, background: selectionMode ? 'var(--navy)' : '#fff', color: selectionMode ? '#fff' : '#1d1d1f', border: `1px solid ${selectionMode ? 'var(--navy)' : 'var(--gray-200)'}`, borderRadius: 6, cursor: 'pointer' }}>
                  <MousePointer2 size={14} />
                </button>
                <button
                  onClick={openUploadPanel}
                  title="Upload images"
                  style={{ display: 'inline-flex', alignItems: 'center', padding: 8, background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                  <Upload size={14} />
                </button>
              </div>
            ) : null}
          </div>
          {/* minWidth + header wrap: on a laptop the buttons drop to a second row instead of crushing search to an icon. */}
          <div style={{ flex: isMobile ? 'none' : '1 1 320px', minWidth: isMobile ? 0 : 320, position: 'relative', maxWidth: isMobile ? 'none' : 520, width: '100%' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={isMobile ? 'Search images…' : 'Try "people eating crabs outside" or "lump crab meat"'}
              style={{ width: '100%', padding: '8px 32px 8px 32px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            {search ? (
              <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                <X size={14} color="var(--gray-500)" />
              </button>
            ) : null}
          </div>
          {!isMobile ? (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--gray-500)' }}>
              Sort
              <select
                value={orderBy}
                onChange={(e) => setOrderBy(e.target.value)}
                style={{ padding: '7px 8px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 6, background: '#fff', color: '#1d1d1f', fontFamily: 'inherit', cursor: 'pointer' }}
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="quality">Quality</option>
                <option value="name">Filename</option>
                <option value="uploader">Uploaded by</option>
              </select>
            </label>
          ) : null}
          {!isMobile ? (
            <button
              onClick={openGlossary}
              title="What we call things — the house vernacular"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', fontSize: 13, fontWeight: 600, background: '#fff', color: '#1d1d1f', border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer' }}
            >
              <BookOpen size={14} /> Vernacular
            </button>
          ) : null}
          {!isMobile ? (
            <button
              onClick={() => {
                if (selectionMode) { exitSelection(); }
                else { setSelectionMode(true); }
              }}
              title={selectionMode ? 'Exit selection mode' : 'Select multiple images for bulk tagging'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', fontSize: 13, fontWeight: 600, background: selectionMode ? 'var(--navy)' : '#fff', color: selectionMode ? '#fff' : '#1d1d1f', border: `1px solid ${selectionMode ? 'var(--navy)' : 'var(--gray-200)'}`, borderRadius: 6, cursor: 'pointer' }}
            >
              <MousePointer2 size={14} /> {selectionMode ? 'Done' : 'Select'}
            </button>
          ) : null}
          {/* Fix a misspelled word everywhere at once (names, alt text, tags). Admin/Manager. */}
          {!isMobile && (userRoles.includes('Admin') || userRoles.includes('Manager')) ? (
            <button
              onClick={async () => {
                const find = (window.prompt('Find which word? (whole word, in names, descriptions and tags)', '') || '').trim();
                if (!find) return;
                const replace = (window.prompt(`Replace "${find}" with:`, '') || '').trim();
                if (!replace) return;
                try {
                  const dry = await neonQuery('media_assets_replace_word', { find, replace, apply: false });
                  if (!dry.count) { window.alert(`No items have the word "${find}".`); return; }
                  if (!window.confirm(`Change "${find}" to "${replace}" on ${dry.count} item${dry.count === 1 ? '' : 's'}?\n\nFor example:\n${(dry.examples || []).join('\n')}`)) return;
                  const done = await neonQuery('media_assets_replace_word', { find, replace, apply: true });
                  window.alert(`Fixed ${done.changed} item${done.changed === 1 ? '' : 's'}.`);
                  fetchPage(true);
                } catch (e) { window.alert('Could not do that: ' + e.message); }
              }}
              title="Fix a misspelled word on every item at once"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', fontSize: 13, fontWeight: 600, background: '#fff', color: '#1d1d1f', border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer' }}
            >
              <Edit2 size={14} /> Fix a word
            </button>
          ) : null}
          {/* Ask guests for THEIR photos: sends an upload link, nothing to pick first. */}
          <button
            onClick={() => setSendItems([])}
            title="Ask someone to send us their photos and videos"
            aria-label="Ask for photos"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: isMobile ? 10 : '8px 14px', fontSize: 13, fontWeight: 600, background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer' }}
          >
            <ImagePlus size={isMobile ? 18 : 14} />{isMobile ? null : ' Ask for photos'}
          </button>
          {!isMobile ? (
            <button
              onClick={openUploadPanel}
              title="Upload images — set brand, name and category in the panel first, then pick files or a folder"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', fontSize: 13, fontWeight: 600, background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
            >
              <Upload size={14} /> Upload
            </button>
          ) : null}
          <input
            ref={folderInputRef}
            type="file"
            webkitdirectory=""
            directory=""
            multiple
            onChange={onFilesPicked}
            style={{ display: 'none' }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept={isMobile
              ? 'image/*,video/*'
              : 'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif,video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm'}
            multiple
            onChange={onFilesPicked}
            style={{ display: 'none' }}
          />
        </header>

        <div ref={contentScrollRef} style={{ flex: 1, overflowY: 'auto', padding: isMobile ? (selectionMode ? '4px 4px 96px' : 4) : 20, background: '#f5f5f7' }}>
          {uploadPanelOpen ? (
            <UploadPanel
              queue={uploadQueue}
              brand={uploadBrand}
              setBrand={setUploadBrand}
              category={uploadCategory}
              setCategory={setUploadCategory}
              subcategory={uploadSubcategory}
              setSubcategory={setUploadSubcategory}
              name={uploadName}
              setName={setUploadName}
              ready={uploadReady}
              gateMsg={uploadGateMsg}
              tags={uploadTags}
              setTags={setUploadTags}
              tagInput={uploadTagInput}
              setTagInput={setUploadTagInput}
              normalizeTag={normalizeTag}
              presets={tagPresets}
              onManagePresets={() => setPresetsOpen(true)}
              onTagAdded={applyTagToLastBatch}
              lastBatchCount={lastBatchIds.length}
              onPickFiles={() => fileInputRef.current?.click()}
              onPickFolder={() => folderInputRef.current?.click()}
              onClear={clearCompletedUploads}
              onClose={() => {
                setUploadPanelOpen(false); setUploadTags([]); setUploadTagInput('');
                setUploadName(''); setUploadCategory(''); setUploadSubcategory(''); setUploadGateMsg(null);
              }}
            />
          ) : null}
          {vocabHits.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12, padding: '9px 12px', background: '#fff', border: '1px solid #cfe0ef', borderRadius: 6 }}>
              <BookOpen size={14} color="var(--navy)" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>We call that</span>
              {vocabHits.map((h) => {
                const active = tagFilter.includes(h.term);
                return (
                  <button
                    key={h.term}
                    title={h.definition || ''}
                    onClick={() => setTagFilter(active ? tagFilter.filter((t) => t !== h.term) : [...tagFilter, h.term])}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', borderRadius: 4, cursor: 'pointer',
                      fontSize: 12, fontWeight: 600,
                      background: active ? 'var(--navy)' : '#eef4fa',
                      color: active ? '#fff' : 'var(--navy)',
                      border: `1px solid ${active ? 'var(--navy)' : '#cfe0ef'}`,
                    }}
                  >
                    {h.label}
                    <span style={{ fontWeight: 400, opacity: 0.75 }}>{h.asset_count}</span>
                    {active ? <X size={11} /> : null}
                  </button>
                );
              })}
              <span style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>
                {tagFilter.some((t) => vocabHits.some((h) => h.term === t)) ? 'Showing only these.' : 'Already at the top — click to show only these.'}
              </span>
            </div>
          ) : null}
          {error ? <div style={{ color: '#c33', padding: 12, marginBottom: 12, background: '#fff5f5', border: '1px solid #ffd6d6', borderRadius: 6 }}>{error}</div> : null}
          {!loading && items.length === 0 ? <div style={{ padding: 20, color: 'var(--gray-500)' }}>No images match the current filters.</div> : null}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: isMobile ? 3 : 14 }}>
            {items.map((m, idx) => (
              <Tile
                key={m.id}
                item={m}
                onStar={() => toggleStar(m)}
                onOpen={() => setOpenItem(m)}
                selectionMode={selectionMode}
                selected={selectedIds.has(m.id)}
                onToggleSelect={(e) => toggleSelect(m, idx, e.shiftKey)}
                compact={isMobile}
              />
            ))}
          </div>
          {items.length >= PAGE_SIZE && total === null ? (
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button onClick={() => fetchPage(false)} disabled={loading} style={{ padding: '10px 24px', fontSize: 13, background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                {loading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : null}
        </div>
        {isDragging ? (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(255, 127, 0, 0.12)',
            border: '3px dashed var(--orange)', borderRadius: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none', zIndex: 50, margin: 8,
          }}>
            <div style={{ padding: '14px 22px', background: '#fff', border: '1px solid var(--orange)', borderRadius: 8, fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>
              <Upload size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Drop images to upload
            </div>
          </div>
        ) : null}
        {selectionMode ? (
          <BulkActionBar
            count={selectedIds.size}
            busy={bulkBusy}
            message={bulkMessage}
            popover={bulkPopover}
            setPopover={setBulkPopover}
            onClear={clearSelection}
            onDone={exitSelection}
            tagOptions={tagCounts}
            tagsOnSelection={tagsOnSelection}
            brandsOnSelection={brandsOnSelection}
            onApplyAdd={(tags) => runBulkTag({ addTags: tags })}
            onApplyRemove={(tags) => runBulkTag({ removeTags: tags })}
            presets={tagPresets}
            onManagePresets={() => { setBulkPopover(null); setPresetsOpen(true); }}
            onApplySetBrand={runBulkSetBrand}
            onApplySetUploader={runBulkSetUploader}
            onSend={() => setSendItems(items.filter((m) => selectedIds.has(m.id)))}
            users={users}
          />
        ) : null}
      </main>

      {sendItems ? <SendMediaModal items={sendItems} onClose={() => setSendItems(null)} /> : null}
      {presetsOpen ? <TagPresetsManager presets={tagPresets} adminId={user?.id} onChanged={loadTagPresets} onClose={() => setPresetsOpen(false)} /> : null}
      {openItem ? <Detail item={openItem} users={users} presets={tagPresets} onManagePresets={() => setPresetsOpen(true)} onStar={() => toggleStar(openItem)} onArchive={() => toggleArchive(openItem)} onSave={(fields) => saveEdit(openItem.id, fields)} onClose={() => setOpenItem(null)} onEditWithAI={() => setAiEditFor(openItem)} onChanged={() => fetchPage(true)} onSend={() => setSendItems([openItem])} onPrev={openPrev} onNext={openNext} position={openIdx >= 0 ? { index: openIdx + 1, total: total != null ? total : `${items.length}+` } : null} /> : null}
      {aiEditFor ? <MediaAIEditModal item={aiEditFor} onClose={() => setAiEditFor(null)} onSaved={(row, info) => { setAiEditFor(null); setOpenItem(null); fetchPage(true); }} /> : null}
      {glossary !== null ? (
        <GlossaryDrawer
          terms={glossary}
          onClose={() => setGlossary(null)}
          onPick={(term) => { setGlossary(null); setSearch(''); setTagFilter([term]); }}
        />
      ) : null}
    </div>
  );
}

// ── Glossary drawer ──────────────────────────────────────────────────────────
// Read-only view of the house vernacular, shown where people actually work.
// Editing lives in Marketing > Media Intake > Vernacular; putting the reference
// here means nobody has to remember whether we say UGC or "customer photo" while
// they are mid-search.
function GlossaryDrawer({ terms, onClose, onPick }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 950, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(460px, 100%)', background: '#fff', height: '100%', overflowY: 'auto', boxShadow: '-2px 0 12px rgba(0,0,0,0.15)' }}>
        <div style={{ position: 'sticky', top: 0, background: '#fff', padding: '14px 16px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <BookOpen size={16} color="var(--navy)" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>What we call things</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>Click a term to see everything filed under it</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><X size={16} color="var(--gray-500)" /></button>
        </div>
        <div style={{ padding: 14 }}>
          {!terms.length ? (
            <div style={{ fontSize: 13, color: 'var(--gray-500)', lineHeight: 1.6 }}>
              Nothing defined yet. Add the words your team actually uses in
              <strong style={{ color: 'var(--navy)' }}> Marketing &rsaquo; Media Intake &rsaquo; Vernacular</strong>,
              and they will start working in this search box.
            </div>
          ) : terms.map((t) => (
            <div key={t.id} style={{ paddingBottom: 12, marginBottom: 12, borderBottom: '1px solid #f1f1f3' }}>
              <button
                onClick={() => onPick(t.term)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}
              >
                <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>{t.label}</span>
                <code style={{ fontSize: 11, background: '#eef4fa', color: 'var(--navy)', padding: '1px 5px', borderRadius: 3 }}>{t.term}</code>
                <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{t.asset_count}</span>
              </button>
              {t.definition ? <div style={{ fontSize: 12.5, color: 'var(--gray-500)', lineHeight: 1.55 }}>{t.definition}</div> : null}
              {(t.aliases || []).length ? (
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 5 }}>
                  <strong style={{ color: 'var(--navy)' }}>Also search:</strong> {t.aliases.join(' · ')}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Upload panel ─────────────────────────────────────────────────────────────
function UploadPanel({ presets = [], onManagePresets, queue, brand, setBrand, category, setCategory, subcategory, setSubcategory, name, setName, ready, gateMsg, tags, setTags, tagInput, setTagInput, normalizeTag, onTagAdded, lastBatchCount = 0, onPickFiles, onPickFolder, onClear, onClose }) {
  const isMobile = useIsMobile();
  const active = queue.filter((q) => q.status === 'queued' || q.status === 'resizing' || q.status === 'uploading').length;
  const done = queue.filter((q) => q.status === 'done').length;
  const errored = queue.filter((q) => q.status === 'error').length;
  // Promote the typed text to a committed chip AND apply it to the batch the
  // user just uploaded (drop-first, type-after). The upload routine also folds
  // any pending text into the next batch, so a tag is never lost.
  const addTag = () => {
    const t = normalizeTag(tagInput);
    setTagInput('');
    if (!t) return;
    if (!tags.includes(t)) setTags([...tags, t]);
    onTagAdded?.(t);
  };

  // ⚠️ On a phone every row STACKS: label over control over hint. The desktop
  // layout puts all three on one line, and the hint on the Name row carries
  // whiteSpace:'nowrap' — at 390px that hint took the row and left the input
  // about 30px wide, so you could not type a name, which is the field the
  // upload is gated on. Measured in a 390px viewport, not guessed.
  // (Rebecca, travelling, 2026-09-04: "the media library is not mobile friendly".)
  const rowStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'stretch' : 'center',
    gap: isMobile ? 6 : 10,
    padding: isMobile ? '12px 14px' : '10px 14px',
    borderBottom: '1px solid #f1f1f3',
  };
  const hintStyle = { fontSize: isMobile ? 12 : 11, color: 'var(--gray-500)', whiteSpace: isMobile ? 'normal' : 'nowrap' };
  const fieldStyle = {
    flex: 1, minWidth: 0, width: isMobile ? '100%' : undefined, boxSizing: 'border-box',
    padding: isMobile ? '10px 12px' : '6px 8px', fontSize: isMobile ? 16 : 12,
    border: '1px solid var(--gray-200)', borderRadius: isMobile ? 8 : 4,
    fontFamily: 'inherit', color: '#1d1d1f',
  };

  return (
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, marginBottom: 16, overflow: 'hidden' }}>
      {/* Row 1 - title + close */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#fafafa', borderBottom: '1px solid var(--gray-200)' }}>
        <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: isMobile ? 15 : 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Upload size={isMobile ? 16 : 14} /> Upload
        </div>
        {!isMobile ? (
          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
            Fill in Brand, Name and Category, then pick files or a folder. Images (JPG/PNG/WebP/HEIC) and videos (MP4/MOV/WebM).
          </div>
        ) : null}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {done > 0 ? <button onClick={onClear} style={{ padding: isMobile ? '8px 12px' : '4px 10px', fontSize: isMobile ? 13 : 11, background: '#fff', color: 'var(--gray-500)', border: '1px solid var(--gray-200)', borderRadius: 4, cursor: 'pointer' }}>Clear {done} done</button> : null}
          <button onClick={onClose} title="Close upload panel" aria-label="Close upload panel" style={{ background: 'none', border: 'none', padding: isMobile ? 8 : 2, cursor: 'pointer', display: 'inline-flex' }}><X size={isMobile ? 20 : 14} color="var(--gray-500)" /></button>
        </div>
      </div>
      {isMobile ? (
        <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--gray-500)', background: '#fafafa', borderBottom: '1px solid var(--gray-200)' }}>
          Fill in Brand, Name and Category, then choose photos. Photos straight off an iPhone (HEIC) and videos are fine.
        </div>
      ) : null}

      {/* Row 2 - Brand (required, nothing preselected) */}
      <div style={rowStyle}>
        <ReqLabel done={!!brand} block={isMobile}>Brand</ReqLabel>
        <div style={{ display: 'flex', gap: isMobile ? 8 : 10, alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
          {BRANDS.map((b) => (
            <button key={b.value} onClick={() => setBrand(b.value)} style={brandBtn(brand === b.value, isMobile)}>{b.label}</button>
          ))}
          {!brand && !isMobile ? <span style={{ fontSize: 11, color: '#c33' }}>Pick one</span> : null}
        </div>
        {!brand && isMobile ? <span style={{ fontSize: 12, color: '#c33' }}>Pick one</span> : null}
      </div>

      {/* Row 3 - Name (required). Drives suggested_name; a multi-file batch is
          numbered from it so nobody renames files one at a time afterwards. */}
      <div style={rowStyle}>
        <ReqLabel done={!!String(name || '').trim()} block={isMobile}>Name</ReqLabel>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={isMobile ? 'what these are - e.g. cape charles dock' : 'what these are - e.g. cape charles dock, memorial day feast'}
          style={{ ...fieldStyle, borderColor: String(name || '').trim() ? 'var(--gray-200)' : '#f0b4b4' }}
        />
        <span style={hintStyle}>
          {String(name || '').trim() ? `2+ files will be "${String(name).trim()} 1", "${String(name).trim()} 2"... (a reused name keeps counting up)` : 'Numbered automatically for multi-file uploads'}
        </span>
      </div>

      {/* Row 4 - Category (required) + optional subcategory. Set here instead of
          fixing Claude's guess one asset at a time after the fact. */}
      <div style={{ ...rowStyle, flexWrap: 'wrap' }}>
        <ReqLabel done={!!category} block={isMobile}>Category</ReqLabel>
        <select
          value={category}
          onChange={(e) => { setCategory(e.target.value); setSubcategory(''); }}
          style={{ ...fieldStyle, flex: isMobile ? undefined : '0 0 auto', borderColor: category ? 'var(--gray-200)' : '#f0b4b4', background: '#fff', cursor: 'pointer' }}
        >
          <option value="">Choose a category...</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={subcategory}
          onChange={(e) => setSubcategory(e.target.value)}
          disabled={!category || !(SUBCATEGORIES[category] || []).length}
          style={{ ...fieldStyle, flex: isMobile ? undefined : '0 0 auto', background: category ? '#fff' : '#f5f5f7', cursor: category ? 'pointer' : 'not-allowed' }}
        >
          <option value="">Subcategory - let Claude pick</option>
          {(SUBCATEGORIES[category] || []).map((sc) => <option key={sc} value={sc}>{sc}</option>)}
        </select>
        <span style={hintStyle}>Overrides auto-classify. Tags, alt text and quality still come from Claude.</span>
      </div>

      {/* Row 5 - pre-set Tags */}
      <div style={{ ...rowStyle, alignItems: isMobile ? 'stretch' : 'flex-start' }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-500)', width: isMobile ? 'auto' : 60, paddingTop: isMobile ? 0 : 6 }}>
          Tags<span style={{ display: isMobile ? 'inline' : 'block', fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 10 }}>{isMobile ? ' - optional' : 'optional'}</span>
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
              onBlur={() => addTag()}
              placeholder={isMobile ? 'add a tag - e.g. shoot-2026-09' : 'add a tag and press Enter - e.g. shoot-2026-05, jess-pics'}
              style={fieldStyle}
            />
            <button type="button" onClick={addTag} title="Add tag" aria-label="Add tag" style={{ padding: isMobile ? '10px 14px' : '6px 10px', fontSize: 12, background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: isMobile ? 8 : 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
              <Plus size={isMobile ? 16 : 12} />
            </button>
            {tags.length ? <button type="button" onClick={() => setTags([])} title="Clear all pre-set tags" style={{ padding: isMobile ? '10px 12px' : '6px 10px', fontSize: 11, background: '#fff', color: 'var(--gray-500)', border: '1px solid var(--gray-200)', borderRadius: isMobile ? 8 : 4, cursor: 'pointer', flexShrink: 0 }}>Clear</button> : null}
          </div>
          <PresetChips presets={presets} onManage={onManagePresets}
            onPick={(p) => { const add = (p.tags || []).filter((t) => !tags.includes(t)); setTags([...tags, ...add]); add.forEach((t) => onTagAdded && onTagAdded(t)); }} />
          {tags.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
              {tags.map((t) => (
                <span key={t} style={{ background: '#fff4e6', padding: isMobile ? '5px 6px 5px 11px' : '2px 4px 2px 8px', borderRadius: 3, fontSize: isMobile ? 13 : 11, color: '#7a3500', display: 'inline-flex', alignItems: 'center', gap: 2, border: '1px solid #ffd6a3' }}>
                  {t}
                  <button onClick={() => setTags(tags.filter((x) => x !== t))} aria-label={`Remove tag ${t}`} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: isMobile ? 4 : 1, display: 'inline-flex', color: '#7a3500' }}>
                    <X size={isMobile ? 14 : 11} />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: isMobile ? 12 : 11, color: 'var(--gray-500)', marginTop: 6 }}>
              Optional - merged with Claude's auto-detected tags. Works either way: set a tag first, or {isMobile ? 'add the tag after' : 'drop files and add the tag after'}{lastBatchCount ? ` (it'll also tag the ${lastBatchCount} you just uploaded)` : ''}.
            </div>
          )}
        </div>
      </div>

      {/* Row 6 - pick files / pick folder.
          ⚠️ "Choose folder..." is NOT offered on a phone. webkitdirectory is a
          desktop-only affordance - iOS Safari accepts the attribute and then
          ignores it, so the button opens the ordinary file sheet or nothing at
          all. A button that silently does the wrong thing is worse than one
          that is not there. */}
      <div style={{ ...rowStyle, background: '#fafafa', borderBottom: queue.length ? '1px solid var(--gray-200)' : 'none' }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-500)', width: isMobile ? 'auto' : 60 }}>Source</span>
        <div style={{ display: 'flex', gap: 10, width: isMobile ? '100%' : 'auto' }}>
          <button onClick={onPickFiles} disabled={!ready} title={ready ? '' : 'Set Brand, Name and Category first'}
            style={{ flex: isMobile ? 1 : '0 0 auto', padding: isMobile ? '14px 16px' : '6px 14px', fontSize: isMobile ? 16 : 13, background: ready ? 'var(--orange)' : '#e8e8ec', color: ready ? '#fff' : 'var(--gray-500)', border: 'none', borderRadius: isMobile ? 8 : 4, cursor: ready ? 'pointer' : 'not-allowed', fontWeight: 600 }}>
            {isMobile ? 'Choose photos or videos' : 'Choose files...'}
          </button>
          {!isMobile ? (
            <button onClick={onPickFolder} disabled={!ready} title={ready ? '' : 'Set Brand, Name and Category first'} style={{ padding: '6px 14px', fontSize: 13, background: '#fff', color: ready ? 'var(--navy)' : 'var(--gray-500)', border: `1px solid ${ready ? 'var(--navy)' : 'var(--gray-200)'}`, borderRadius: 4, cursor: ready ? 'pointer' : 'not-allowed', fontWeight: 600 }}>Choose folder...</button>
          ) : null}
        </div>
        <span style={{ ...hintStyle, color: ready ? 'var(--gray-500)' : '#c33' }}>
          {ready
            ? (isMobile ? 'Pick as many as you like. They upload one after another, so leave this page open.' : 'or drag and drop anywhere on this page')
            : 'Fill in Brand, Name and Category to enable uploading'}
        </span>
      </div>
      {gateMsg ? (
        <div style={{ padding: '8px 14px', fontSize: 12, color: '#8a1c1c', background: '#fdeaea', borderBottom: '1px solid #f5c6c6' }}>{gateMsg}</div>
      ) : null}
      {queue.length ? (
        <>
          <div style={{ padding: '6px 14px', fontSize: isMobile ? 12 : 11, color: 'var(--gray-500)', background: '#fafafa', borderBottom: '1px solid var(--gray-200)' }}>
            {active > 0 ? `${active} in progress · ` : ''}{done} done{errored ? ` · ${errored} error${errored === 1 ? '' : 's'}` : ''}
          </div>
          <div style={{ maxHeight: isMobile ? 200 : 280, overflowY: 'auto' }}>
            {queue.map((q) => <UploadRow key={q.id} item={q} />)}
          </div>
        </>
      ) : null}
    </div>
  );
}

// A required-field label that turns green once the field is filled, so the
// gate reads at a glance instead of only surfacing on a failed drop.
function ReqLabel({ children, done, block = false }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: done ? '#1a7f37' : '#c33', width: block ? 'auto' : 60, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      {children}<span aria-hidden="true">*</span>
    </span>
  );
}

function UploadRow({ item }) {
  const { icon, color } = statusDisplay(item.status);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', fontSize: 12, borderBottom: '1px solid #f3f3f5' }}>
      <div style={{ color, flexShrink: 0, width: 18, display: 'flex', alignItems: 'center' }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontWeight: 500, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
        {item.message ? <div style={{ fontSize: 11, color: item.status === 'error' ? '#c33' : 'var(--gray-500)' }}>{item.message}</div> : null}
      </div>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', flexShrink: 0 }}>
        {(item.size / 1024).toFixed(0)} KB
      </div>
    </div>
  );
}

function statusDisplay(status) {
  if (status === 'queued') return { icon: <Loader2 size={14} style={{ opacity: 0.4 }} />, color: 'var(--gray-500)' };
  if (status === 'resizing' || status === 'uploading') return { icon: <Loader2 size={14} className="spin" style={{ animation: 'spin 1s linear infinite' }} />, color: 'var(--orange)' };
  if (status === 'done') return { icon: <CheckCircle2 size={14} />, color: '#1a7f37' };
  if (status === 'error') return { icon: <AlertCircle size={14} />, color: '#c33' };
  return { icon: null, color: 'var(--gray-500)' };
}

function brandBtn(active, isMobile = false) {
  return {
    // A 4x10px button is a ~22px tap target; Apple's guidance is 44px. On a
    // phone these go full width of their half of the row instead.
    flex: isMobile ? 1 : undefined,
    padding: isMobile ? '12px 10px' : '4px 10px', fontSize: isMobile ? 14 : 11, fontWeight: 600,
    background: active ? 'var(--navy)' : '#fff',
    color: active ? '#fff' : '#424245',
    border: `1px solid ${active ? 'var(--navy)' : 'var(--gray-200)'}`,
    borderRadius: isMobile ? 8 : 4, cursor: 'pointer',
  };
}

// ── Tile ─────────────────────────────────────────────────────────────────────
function formatDuration(s) {
  if (!s || s < 0) return '';
  const total = Math.round(Number(s));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

// compact = phone layout: a photo-app grid (3 across, no caption block). The details
// are one tap away in the Detail view; on a 390px screen they cost two photos per row.
function Tile({ item, onStar, onOpen, selectionMode = false, selected = false, onToggleSelect, compact = false }) {
  const q = item.quality_score || 0;
  const archived = !!item.archived_at;
  const isVideo = item.media_kind === 'video';
  const isAudio = item.media_kind === 'audio';
  const isGif = isGifAsset(item);
  const isTransparent = isTransparentAsset(item);
  const handleClick = (e) => {
    if (selectionMode) { onToggleSelect?.(e); return; }
    onOpen();
  };
  const borderColor = selected
    ? 'var(--orange)'
    : (archived ? '#ffd6a3' : 'var(--gray-200)');
  const tileBorder = selected ? `3px solid var(--orange)` : `1px solid ${borderColor}`;
  // Compensate for the thicker selected border so tile dimensions stay constant
  const tileMargin = selected ? '-2px' : '0';
  return (
    <div style={{ background: '#fff', borderRadius: compact ? 4 : 8, overflow: 'hidden', border: compact && !selected ? 'none' : tileBorder, margin: compact ? 0 : tileMargin, outline: compact && selected ? '3px solid var(--orange)' : 'none', outlineOffset: -3, display: 'flex', flexDirection: 'column', cursor: 'pointer', opacity: archived && !selected ? 0.65 : 1, userSelect: 'none' }} onClick={handleClick}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', background: '#f0f0f3', ...(isTransparent ? CHECKERBOARD_STYLE : null) }}>
        {/* GIF tiles load the original .gif so they animate right in the grid;
            everything else uses the static JPEG thumbnail. loading="lazy" keeps
            offscreen GIFs from all fetching at once. Transparent assets carry a
            PNG thumb, shown whole (contain) over a checkerboard so the
            transparency reads instead of the logo being cropped/flattened. */}
        <img src={thumbUrl(isGif ? item.r2_original_key : item.r2_thumb_key)} alt={item.alt_text || ''} loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: isTransparent ? 'contain' : 'cover', display: 'block' }} />
        {isGif ? (
          <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.62)', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: 0.6, pointerEvents: 'none' }} title="Animated GIF">GIF</div>
        ) : null}
        {isTransparent ? (
          <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,57,92,0.78)', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: 0.6, pointerEvents: 'none', whiteSpace: 'nowrap' }} title="Has a transparent background">TRANSPARENT</div>
        ) : null}
        {isAudio ? (
          <>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: '50%', width: 52, height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                <Volume2 size={24} />
              </div>
            </div>
            <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.62)', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: 0.6, pointerEvents: 'none' }} title="Audio">AUDIO</div>
            {item.duration_s ? (
              <div style={{ position: 'absolute', ...(compact ? { bottom: 4, right: 4 } : { top: 6, right: 44 }), background: 'rgba(0,0,0,0.7)', color: '#fff', padding: '2px 6px', borderRadius: 3, fontSize: 10, fontFamily: 'monospace' }}>
                {formatDuration(item.duration_s)}
              </div>
            ) : null}
          </>
        ) : null}
        {isVideo ? (
          <>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: '50%', width: compact ? 32 : 52, height: compact ? 32 : 52, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                <span style={{ fontSize: compact ? 13 : 22, marginLeft: compact ? 2 : 4 }}>▶</span>
              </div>
            </div>
            {item.duration_s ? (
              <div style={{ position: 'absolute', ...(compact ? { bottom: 4, right: 4 } : { top: 6, right: 44 }), background: 'rgba(0,0,0,0.7)', color: '#fff', padding: '2px 6px', borderRadius: 3, fontSize: 10, fontFamily: 'monospace' }}>
                {formatDuration(item.duration_s)}
              </div>
            ) : null}
          </>
        ) : null}
        {compact ? null : <div style={{ position: 'absolute', top: 8, left: 8, background: 'var(--navy)', padding: '4px 9px', borderRadius: 4, fontSize: 14, fontFamily: 'monospace', letterSpacing: 1, lineHeight: 1, boxShadow: '0 1px 4px rgba(0,0,0,0.35)' }}>
          <span style={{ color: 'var(--orange)' }}>{'★'.repeat(q)}</span><span style={{ color: 'rgba(255,255,255,0.35)' }}>{'★'.repeat(5 - q)}</span>
        </div>}
        {archived ? <div style={{ position: 'absolute', top: 8, right: 44, background: 'rgba(176,93,0,0.92)', color: '#fff', padding: '3px 8px', fontSize: 10, borderRadius: 3, fontWeight: 600, textTransform: 'uppercase' }}>archived</div> : null}
        {selectionMode ? (
          <div style={{ position: 'absolute', top: compact ? 4 : 6, right: compact ? 4 : 6, background: selected ? 'var(--orange)' : 'rgba(255,255,255,0.92)', border: `2px solid ${selected ? 'var(--orange)' : '#fff'}`, borderRadius: '50%', width: compact ? 24 : 28, height: compact ? 24 : 28, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
            {selected ? <Check size={16} color="#fff" strokeWidth={3} /> : null}
          </div>
        ) : compact && !item.human_starred ? null : (
          <button onClick={(e) => { e.stopPropagation(); onStar(); }}
            title={item.human_starred ? 'Unstar' : 'Star'}
            style={{ position: 'absolute', top: compact ? 4 : 6, right: compact ? 4 : 6, background: item.human_starred ? '#ffd60a' : 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: compact ? 22 : 28, height: compact ? 22 : 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Star size={14} fill={item.human_starred ? '#1d1d1f' : 'none'} color={item.human_starred ? '#1d1d1f' : 'var(--gray-500)'} />
          </button>
        )}
        {compact ? null : <div style={{ position: 'absolute', bottom: 6, left: 8, background: 'rgba(0,0,0,0.55)', color: '#fff', padding: '2px 6px', borderRadius: 3, fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {brandShort(item.brand)}
        </div>}
        {!compact && item.width && item.height ? (
          <div style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.55)', color: '#fff', padding: '2px 6px', borderRadius: 3, fontSize: 9, fontFamily: 'monospace', letterSpacing: 0.3 }}>
            {item.width}×{item.height}
          </div>
        ) : null}
      </div>
      {compact ? null : <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11 }}>
        <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{item.category} <span style={{ color: 'var(--gray-500)', fontWeight: 400 }}>/ {item.subcategory}</span></div>
        <div style={{ fontFamily: 'monospace', color: 'var(--orange)', fontSize: 10, wordBreak: 'break-all' }}>{item.suggested_name}</div>
        <div style={{ color: '#1d1d1f', fontStyle: 'italic', fontSize: 11, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.alt_text}</div>
        {item.uploaded_by_name ? (
          <div style={{ marginTop: 2, fontSize: 10, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`Uploaded by ${item.uploaded_by_name}`}>
            <UserRound size={10} style={{ flexShrink: 0 }} /> {item.uploaded_by_name}
          </div>
        ) : null}
      </div>}
    </div>
  );
}

// ── Detail modal (view + edit) ───────────────────────────────────────────────
// Circular prev/next buttons overlaid on the media pane.
const detailNavArrowStyle = (side) => ({
  position: 'absolute', [side]: 10, top: '50%', transform: 'translateY(-50%)',
  width: 44, height: 44, borderRadius: '50%', border: 'none',
  background: 'rgba(255,255,255,0.18)', color: '#fff', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5,
});

function Detail({ item, users = [], presets = [], onManagePresets, onStar, onArchive, onSave, onClose, onEditWithAI, onChanged, onSend, onPrev, onNext, position }) {
  const isMobile = useIsMobile();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => ({
    category: item.category || '',
    subcategory: item.subcategory || '',
    suggested_name: item.suggested_name || '',
    alt_text: item.alt_text || '',
    tags: [...(item.tags || [])],
    quality_score: item.quality_score || 3,
    usable_for: [...(item.usable_for || [])],
    notes: item.notes || '',
    brand: item.brand || 'crab-place',
  }));
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);  // size label currently being prepared
  const downloadRef = useRef(null);
  const [copiedKind, setCopiedKind] = useState(null); // 'original' | 'thumb'
  const [wmOpen, setWmOpen] = useState(false);        // video watermark export modal
  const [sendingGiphy, setSendingGiphy] = useState(false);

  // Send this GIF to the Crab Place Giphy channel (needs an Upload Production Key;
  // the giphy-proxy returns a clear message if it isn't configured yet).
  const sendToGiphy = async () => {
    if (sendingGiphy) return;
    setSendingGiphy(true);
    try {
      const res = await fetch('/.netlify/functions/giphy-proxy', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'start-upload', assetId: item.id, adminId: getCurrentUser()?.id }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      if (d.alreadyOnGiphy) alert('This GIF is already on your Giphy channel.');
      else alert('Queued to send to Giphy. Track it on Marketing → Giphy.');
    } catch (e) { alert('Send to Giphy failed: ' + e.message); }
    finally { setSendingGiphy(false); }
  };
  const [savingResize, setSavingResize] = useState(null); // edge currently being saved to library
  const [regenState, setRegenState] = useState(null);      // null | 'working' | 'done' | 'error'
  const [capture, setCapture] = useState(null);            // { state: 'working'|'done'|'error', text }
  const videoRef = useRef(null);

  // Re-extract a real (non-black) poster frame server-side for this video. Phone
  // HEVC clips can't be frame-grabbed in the browser at upload, so they land with
  // a neutral placeholder thumbnail (the "black tile"). This fires the ffmpeg
  // background fn, then polls r2_thumb_key until it swaps to the extracted frame.
  const regenThumbnail = async () => {
    if (regenState === 'working') return;
    setRegenState('working');
    try {
      const priorThumb = item.r2_thumb_key;
      const base = window.location.hostname === 'localhost' ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
      const res = await fetch(`${base}/media-poster-regen-background`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId: item.id }),
      });
      // Background fns return 202 with no body; anything ≥400 is a real failure.
      if (res.status >= 400) throw new Error(`HTTP ${res.status}`);
      // Poll for up to ~3 min — a 4K HEVC clip range-seeked over HTTP (plus the
      // one-time ffmpeg download on a cold function) can take ~2 min to extract.
      let newKey = null;
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const rows = await neonQuery('media_asset_get', { id: item.id });
          const k = rows?.[0]?.r2_thumb_key;
          if (k && k !== priorThumb) { newKey = k; break; }
        } catch { /* keep polling */ }
      }
      if (!newKey) throw new Error('still working in the background — check back in a minute and refresh');
      setRegenState('done');
      if (onChanged) onChanged();  // refresh the grid so the new frame shows everywhere
      setTimeout(() => setRegenState(null), 2500);
    } catch (e) {
      setRegenState('error');
      alert(`Regenerate thumbnail failed: ${e.message}`);
      setTimeout(() => setRegenState(null), 500);
    }
  };

  // Grab the frame currently on screen and file it as its own Media Library
  // image — no new tab, no download, no re-upload. The still inherits the clip's
  // brand, category, tags and quality server-side (see register-frame) and is
  // named "<clip name>-N", so a clip can yield as many stills as you like.
  //
  // The frame is drawn from a SEPARATE hidden <video> carrying
  // crossOrigin="anonymous" rather than from the one that's playing. r2-fetch
  // 302s to a cross-origin R2 presigned URL, and a plain (non-CORS) video taints
  // the canvas, so toDataURL would throw. Requesting CORS on the visible player
  // would fix the taint but breaks playback outright if R2 ever stops sending
  // the header — capturing from a throwaway element keeps that failure confined
  // to this button. R2 currently returns Access-Control-Allow-Origin for the
  // deployed site; local dev is not in that allowlist, so expect this to work on
  // the deployed app only.
  const captureFrame = async () => {
    if (capture?.state === 'working') return;
    const src = videoRef.current;
    const at = src && isFinite(src.currentTime) ? src.currentTime : 0;
    setCapture({ state: 'working', text: 'Capturing…' });
    try {
      const vid = document.createElement('video');
      vid.crossOrigin = 'anonymous';
      vid.muted = true;
      vid.playsInline = true;
      vid.preload = 'auto';
      // The proxy when there is one: a canvas grab from a codec the browser cannot
      // decode returns a black frame, which is how these assets got their posters.
      vid.src = `/.netlify/functions/r2-fetch?key=${encodeURIComponent(item.r2_playable_key || item.r2_original_key)}`;

      await new Promise((resolve, reject) => {
        const t = setTimeout(() => reject(new Error('timed out loading the video')), 30000);
        vid.onloadeddata = () => { clearTimeout(t); resolve(); };
        vid.onerror = () => { clearTimeout(t); reject(new Error('could not load the video for capture')); };
      });
      // Seek to the exact spot the player is sitting on. Browsers range-request,
      // so this pulls only the chunk it needs rather than the whole file.
      await new Promise((resolve) => {
        const done = () => resolve();
        const t = setTimeout(done, 15000);
        vid.onseeked = () => { clearTimeout(t); done(); };
        try { vid.currentTime = Math.min(at, Math.max(0, (vid.duration || at) - 0.05)); }
        catch { clearTimeout(t); done(); }
      });

      const w = vid.videoWidth, h = vid.videoHeight;
      if (!w || !h) throw new Error('the browser could not decode a frame from this clip');
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(vid, 0, 0, w, h);
      let jpeg;
      try {
        jpeg = canvas.toDataURL('image/jpeg', 0.92);
      } catch {
        // SecurityError = the canvas is tainted, i.e. R2 did not return CORS
        // headers for this origin. Say so plainly instead of "capture failed".
        throw new Error('this browser blocked reading the frame (video CORS). Try the deployed site rather than local dev.');
      }

      const base = window.location.hostname === 'localhost' ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
      const res = await fetch(`${base}/media-library-upload`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'register-frame',
          sourceAssetId: item.id,
          originalBase64: jpeg.slice(jpeg.indexOf(',') + 1),
          width: w, height: h,
          admin_id: getCurrentUser()?.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);

      setCapture({ state: 'done', text: data.duplicate ? `Already saved as ${data.row?.suggested_name}` : `Saved as ${data.row?.suggested_name}` });
      if (onChanged) onChanged();   // the still shows up in the grid immediately
      setTimeout(() => setCapture((c) => (c?.state === 'done' ? null : c)), 4000);
    } catch (e) {
      setCapture({ state: 'error', text: `Capture failed: ${e.message}` });
      setTimeout(() => setCapture((c) => (c?.state === 'error' ? null : c)), 8000);
    }
  };

  const saveResizedToLibrary = async (edge) => {
    if (savingResize) return;
    setSavingResize(edge);
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
      const res = await fetch(`${base}/media-watermark`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'save-image', assetId: item.id, edge, createdByAdminId: getCurrentUser()?.id }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.error) throw new Error(d.error || `error ${res.status}`);
      setDownloadOpen(false);
      if (onChanged) onChanged();
      alert(`Saved a ${edge}px copy to the Media Library.`);
    } catch (e) { alert(`Save failed: ${e.message}`); }
    finally { setSavingResize(null); }
  };

  const publicUrl = (key) => key
    ? `https://cpx-prototype.netlify.app/.netlify/functions/r2-image?key=${encodeURIComponent(key)}`
    : '';
  const copyUrl = async (kind, url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKind(kind);
      setTimeout(() => setCopiedKind((c) => (c === kind ? null : c)), 1500);
    } catch {
      window.prompt('Copy URL:', url);
    }
  };

  useEffect(() => {
    if (!downloadOpen) return;
    const onDocClick = (e) => { if (downloadRef.current && !downloadRef.current.contains(e.target)) setDownloadOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [downloadOpen]);

  useEffect(() => {
    setDraft({
      category: item.category || '', subcategory: item.subcategory || '',
      suggested_name: item.suggested_name || '', alt_text: item.alt_text || '',
      tags: [...(item.tags || [])], quality_score: item.quality_score || 3,
      usable_for: [...(item.usable_for || [])], notes: item.notes || '',
      brand: item.brand || 'crab-place',
    });
  }, [item.id, item.category, item.subcategory, item.suggested_name, item.alt_text, item.quality_score, item.notes, item.brand, item.tags, item.usable_for]);

  // Navigating to another asset drops any in-progress edit UI (the draft above
  // resets too) so the modal always opens the next item in view mode.
  useEffect(() => {
    setEditing(false); setTagInput(''); setDownloadOpen(false); setWmOpen(false); setCapture(null);
  }, [item.id]);

  // ← / → flip through the library; Esc closes. Ignored while typing in a field
  // so arrow keys still move the cursor inside inputs.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      if (typing) return;
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && onPrev) onPrev();
      else if (e.key === 'ArrowRight' && onNext) onNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onPrev, onNext, onClose]);

  const archived = !!item.archived_at;

  const handleSave = async () => {
    setSaving(true);
    const fields = {};
    if (draft.category !== item.category) fields.category = draft.category;
    if (draft.subcategory !== item.subcategory) fields.subcategory = draft.subcategory;
    if (draft.suggested_name !== item.suggested_name) fields.suggested_name = draft.suggested_name;
    if (draft.alt_text !== item.alt_text) fields.alt_text = draft.alt_text;
    if (draft.notes !== item.notes) fields.notes = draft.notes;
    if (draft.brand !== item.brand) fields.brand = draft.brand;
    if (draft.quality_score !== item.quality_score) fields.quality_score = draft.quality_score;
    if (JSON.stringify(draft.tags.slice().sort()) !== JSON.stringify((item.tags || []).slice().sort())) fields.tags = draft.tags;
    if (JSON.stringify(draft.usable_for.slice().sort()) !== JSON.stringify((item.usable_for || []).slice().sort())) fields.usable_for = draft.usable_for;
    if (Object.keys(fields).length === 0) { setEditing(false); setSaving(false); return; }
    try { await onSave(fields); setEditing(false); }
    catch (e) { /* error already surfaced */ }
    finally { setSaving(false); }
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/\s+/g, '-');
    if (t && !draft.tags.includes(t)) setDraft({ ...draft, tags: [...draft.tags, t] });
    setTagInput('');
  };

  const isVideo = item.media_kind === 'video';
  const masterEdge = Math.max(item.width || 0, item.height || 0);
  const downloadSizes = [
    { label: 'Original', edge: null },
    { label: 'Large', edge: 1600 },
    { label: 'Medium', edge: 1200 },
    { label: 'Web', edge: 800 },
    { label: 'Email', edge: 600 },
    { label: 'Thumb', edge: 400 },
  ];

  const downloadAt = async (size) => {
    if (downloading) return;
    setDownloading(size.label);
    try {
      // A file the uploader named themselves ("Misty Point Oysters_1332.jpg") downloads
      // under that name. Rebecca, 2026-10-09: a batch name turned every download into
      // "oysters-23" and the variety was gone. Camera names (IMG_1234) still fall back
      // to the library name, which says more than they do.
      const ownName = String(item.original_name || '').replace(/\.[a-z0-9]+$/i, '').trim();
      const cameraName = /^(img|dsc|dscn|pxl|mvi|image|photo|screenshot|screen shot)?[\s_\-]*[\d\s_\-.]*( copy| \d+)?$/i.test(ownName);
      const baseName = (ownName && !cameraName ? ownName : (item.suggested_name || ownName || 'image')).replace(/\.[a-z0-9]+$/i, '');
      const ext = (item.r2_original_key.match(/\.([a-z0-9]+)$/i) || [, 'jpg'])[1];

      // Video → original via r2-fetch (presigned redirect)
      if (isVideo) {
        const filename = item.original_name || `${baseName}.mp4`;
        const a = document.createElement('a');
        a.href = `/.netlify/functions/r2-fetch?key=${encodeURIComponent(item.r2_original_key)}&filename=${encodeURIComponent(filename)}`;
        a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        setDownloadOpen(false);
        return;
      }

      // Original (or requested edge ≥ master) → direct browser download via presigned URL.
      // Bypasses Lambda byte-buffering entirely (Netlify's 6 MB function response cap
      // was 502'ing on > ~5 MB masters via r2-image).
      if (size.edge == null || (masterEdge && size.edge >= masterEdge)) {
        const filename = `${baseName}-original.${ext}`;
        const a = document.createElement('a');
        a.href = `/.netlify/functions/r2-fetch?key=${encodeURIComponent(item.r2_original_key)}&filename=${encodeURIComponent(filename)}`;
        a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        setDownloadOpen(false);
        return;
      }

      // Resize server-side via r2-resize (sharp). Output JPEG fits well under the
      // 6 MB function cap at any of our sizes (Large=1600 → ~200-500 KB).
      const filename = `${baseName}-${size.edge}px.jpg`;
      const a = document.createElement('a');
      a.href = `/.netlify/functions/r2-resize?key=${encodeURIComponent(item.r2_original_key)}&edge=${size.edge}&filename=${encodeURIComponent(filename)}`;
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setDownloadOpen(false);
    } catch (e) {
      alert(`Download failed: ${e.message}`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="cpx-touch" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 0 : 24 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: isMobile ? 0 : 10, maxWidth: isMobile ? '100%' : 1280, width: '100%', height: isMobile ? '100%' : 'auto', maxHeight: isMobile ? '100%' : '92vh', display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: 'hidden' }}>
        <div style={{ flex: isMobile ? '0 0 auto' : 1.4, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: isMobile ? 220 : 400, maxHeight: isMobile ? '45vh' : 'none', position: 'relative' }}>
          {item.media_kind === 'audio' && item.r2_original_key ? (
            // Waveform above, transport below. Without a player you have to
            // download a music bed to find out what it is.
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, padding: 24, width: '100%' }}>
              <img src={thumbUrl(item.r2_thumb_key)} alt={item.alt_text || ''} style={{ maxWidth: '100%', maxHeight: isMobile ? '28vh' : '46vh', objectFit: 'contain', borderRadius: 6 }} />
              <audio
                key={item.id}
                src={`/.netlify/functions/r2-fetch?key=${encodeURIComponent(item.r2_original_key)}`}
                controls
                preload="metadata"
                style={{ width: '100%', maxWidth: 520 }}
              />
            </div>
          ) : item.media_kind === 'video' && item.r2_original_key ? (
            // ⚠️ PLAY THE PROXY WHEN THERE IS ONE. A .mov master can be ProRes or HEVC,
            // which Chrome cannot decode: it plays the audio track and leaves the poster
            // frozen, which reads as a broken player rather than an unsupported file.
            // r2_playable_key is an H.264 copy made by media-video-proxy-background.
            <video
              key={item.id}
              ref={videoRef}
              src={`/.netlify/functions/r2-fetch?key=${encodeURIComponent(item.r2_playable_key || item.r2_original_key)}`}
              poster={thumbUrl(item.r2_thumb_key)}
              controls
              preload="metadata"
              style={{ maxWidth: '100%', maxHeight: isMobile ? '45vh' : '92vh' }}
            />
          ) : isGifAsset(item) && item.r2_original_key ? (
            // Render the original .gif so it animates (the thumbnail is a static JPEG).
            <img src={thumbUrl(item.r2_original_key)} alt={item.alt_text || ''} style={{ maxWidth: '100%', maxHeight: isMobile ? '45vh' : '92vh', objectFit: 'contain' }} />
          ) : isTransparentAsset(item) ? (
            // Transparent asset — checkerboard behind so the transparency is
            // visible (the pane is black; a white logo would vanish, a dark one
            // on the flattened thumb reads as opaque). Prefer the full-res
            // original when it fits under r2-image's ~6MB proxy cap, else the
            // alpha-preserving PNG thumb.
            <img
              src={thumbUrl(Number(item.size_bytes) > 0 && Number(item.size_bytes) < 5_000_000 ? item.r2_original_key : item.r2_thumb_key)}
              alt={item.alt_text || ''}
              style={{ maxWidth: '100%', maxHeight: isMobile ? '45vh' : '92vh', objectFit: 'contain', ...CHECKERBOARD_STYLE }}
            />
          ) : (
            <img src={thumbUrl(item.r2_thumb_key)} alt={item.alt_text || ''} style={{ maxWidth: '100%', maxHeight: isMobile ? '45vh' : '92vh', objectFit: 'contain' }} />
          )}
          {onPrev ? (
            <button onClick={onPrev} title="Previous (←)" style={detailNavArrowStyle('left')}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}>
              <ChevronLeft size={26} />
            </button>
          ) : null}
          {onNext ? (
            <button onClick={onNext} title="Next (→)" style={detailNavArrowStyle('right')}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}>
              <ChevronRight size={26} />
            </button>
          ) : null}
          {position ? (
            <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999, pointerEvents: 'none', letterSpacing: 0.3 }}>
              {position.index} of {position.total}
            </div>
          ) : null}
          {/* A close button where people look for one (Greg, Oct 21, 2026: "no way to close this
              pop up"). On a phone the modal fills the screen, so there is no backdrop to tap, and
              the X in the button row below reads as one more tool. */}
          <button onClick={onClose} title="Close" aria-label="Close"
            style={{ position: 'absolute', top: 10, right: 10, width: 40, height: 40, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 6 }}>
            <X size={22} />
          </button>
        </div>
        <div style={{ flex: 1, padding: isMobile ? 14 : 22, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14, minWidth: isMobile ? 0 : 360, maxWidth: isMobile ? 'none' : 460 }}>
          {/* Phone: buttons on their own row above the name, so the name isn't squeezed into a
              sliver beside seven icons. */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{brandLabel(item.brand)}</div>
              <div style={{ fontFamily: 'monospace', color: 'var(--orange)', fontSize: 13, marginTop: 4, wordBreak: 'break-all' }}>{item.suggested_name}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, position: 'relative', justifyContent: isMobile ? 'space-between' : 'flex-start' }} ref={downloadRef}>
              {!editing ? <IconBtn onClick={() => setEditing(true)} title="Edit" icon={<Edit2 size={14} />} /> : null}
              {!editing && !isVideo && onEditWithAI ? <IconBtn onClick={onEditWithAI} title="Edit with AI" icon={<Wand2 size={14} />} /> : null}
              <IconBtn onClick={onStar} title={item.human_starred ? 'Unstar' : 'Star'} icon={<Star size={14} fill={item.human_starred ? '#1d1d1f' : 'none'} />} active={!!item.human_starred} />
              {!editing && onSend ? <IconBtn onClick={onSend} title="Send by email or text" icon={<Send size={14} />} /> : null}
              <IconBtn onClick={() => isVideo ? setWmOpen(true) : setDownloadOpen((v) => !v)} title={isVideo ? 'Export / watermark' : 'Download'} icon={downloading ? <Loader2 size={14} className="spin" /> : <Download size={14} />} />
              {isVideo ? <IconBtn onClick={captureFrame} title={capture?.state === 'working' ? 'Capturing frame…' : 'Screenshot this frame into the Media Library'} icon={capture?.state === 'working' ? <Loader2 size={14} className="spin" /> : capture?.state === 'done' ? <Check size={14} /> : <Camera size={14} />} active={capture?.state === 'done'} /> : null}
              {isVideo ? <IconBtn onClick={regenThumbnail} title={regenState === 'working' ? 'Regenerating thumbnail…' : regenState === 'done' ? 'Thumbnail updated' : 'Regenerate thumbnail (fix black tile)'} icon={regenState === 'working' ? <Loader2 size={14} className="spin" /> : regenState === 'done' ? <Check size={14} /> : <RotateCcw size={14} />} active={regenState === 'done'} /> : null}
              {isGifAsset(item) ? <IconBtn onClick={sendToGiphy} title="Send to Giphy" icon={sendingGiphy ? <Loader2 size={14} className="spin" /> : <Upload size={14} />} /> : null}
              <IconBtn onClick={onArchive} title={archived ? 'Unarchive' : 'Archive'} icon={archived ? <ArchiveRestore size={14} /> : <Archive size={14} />} />
              <IconBtn onClick={onClose} title="Close" icon={<X size={16} />} />
              {downloadOpen && !isVideo ? (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, padding: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.15)', zIndex: 20, minWidth: 220 }}>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '4px 8px 6px', fontWeight: 600 }}>Download size</div>
                  {downloadSizes.map((s) => {
                    const skip = s.edge != null && masterEdge && s.edge >= masterEdge;
                    const isCurrent = downloading === s.label;
                    const subtitle = s.edge == null
                      ? (masterEdge ? `${masterEdge}px master` : 'master file')
                      : (skip ? `master is ${masterEdge}px — same as original` : `${s.edge}px long edge`);
                    return (
                      <button key={s.label} onClick={() => downloadAt(s)} disabled={!!downloading}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 8px', fontSize: 12, background: 'none', border: 'none', borderRadius: 4, cursor: downloading ? 'wait' : 'pointer', color: '#1d1d1f' }}
                        onMouseEnter={(e) => { if (!downloading) e.currentTarget.style.background = '#f5f5f7'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
                        <div style={{ fontWeight: 600 }}>{isCurrent ? 'Preparing…' : s.label}</div>
                        <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{subtitle}</div>
                      </button>
                    );
                  })}
                  <div style={{ borderTop: '1px solid #eee', margin: '6px 0 2px', paddingTop: 6 }}>
                    <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 8px 4px', fontWeight: 600 }}>Save a copy to Library</div>
                    {downloadSizes.filter((s) => s.edge != null).map((s) => (
                      <button key={'save-' + s.label} onClick={() => saveResizedToLibrary(s.edge)} disabled={!!savingResize}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 8px', fontSize: 12, background: 'none', border: 'none', borderRadius: 4, cursor: savingResize ? 'wait' : 'pointer', color: '#1d1d1f' }}
                        onMouseEnter={(e) => { if (!savingResize) e.currentTarget.style.background = '#f5f5f7'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
                        {savingResize === s.edge ? 'Saving…' : `${s.label} — ${s.edge}px`}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            {wmOpen && isVideo ? <WatermarkExportModal item={item} onClose={() => setWmOpen(false)} onSaved={() => { if (onChanged) onChanged(); }} /> : null}
          </div>

          {capture ? (
            <div style={{ fontSize: 12, padding: '7px 10px', borderRadius: 6, background: capture.state === 'error' ? '#fdeaea' : '#eaf6ee', color: capture.state === 'error' ? '#8a1c1c' : '#15632c', border: `1px solid ${capture.state === 'error' ? '#f5c6c6' : '#bfe3cb'}` }}>
              {capture.text}
            </div>
          ) : null}

          {/* File name */}
          <div>
            <Label>File</Label>
            <div style={{ fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: 'var(--gray-700)', wordBreak: 'break-all' }}>{item.original_name || item.suggested_name}</div>
          </div>

          {/* Quality */}
          <div>
            <Label>Quality</Label>
            {editing ? (
              <div style={{ display: 'flex', gap: 4 }}>
                {[1,2,3,4,5].map((q) => (
                  <button key={q} onClick={() => setDraft({ ...draft, quality_score: q })}
                    style={{ ...qButtonStyle(draft.quality_score === q), padding: '6px 10px' }}>
                    {q}★
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ display: 'inline-block', background: 'var(--navy)', padding: '6px 12px', borderRadius: 4, fontSize: 18, fontFamily: 'monospace', letterSpacing: 2, lineHeight: 1 }}>
                <span style={{ color: 'var(--orange)' }}>{'★'.repeat(item.quality_score || 0)}</span><span style={{ color: 'rgba(255,255,255,0.35)' }}>{'★'.repeat(5 - (item.quality_score || 0))}</span>
              </div>
            )}
          </div>

          {/* Category / Subcategory */}
          <div>
            <Label>Category</Label>
            {editing ? (
              // Side by side, the select takes its natural width and leaves the
              // subcategory input about 30px wide on a 390px screen.
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 6 }}>
                <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} style={selectStyle()}>
                  {['products','plated','lifestyle','ugc','process','people','marketing','other'].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input type="text" value={draft.subcategory} onChange={(e) => setDraft({ ...draft, subcategory: e.target.value })} placeholder="subcategory" style={{ ...selectStyle(), flex: 1 }} />
              </div>
            ) : (
              <div style={{ fontSize: 14 }}>{item.category} <span style={{ color: 'var(--gray-500)' }}>/ {item.subcategory}</span></div>
            )}
          </div>

          {/* Brand */}
          {editing ? (
            <div>
              <Label>Brand</Label>
              <div style={{ display: 'flex', gap: 4 }}>
                {BRANDS.map(({ value: v, label: lbl }) => (
                  <button key={v} onClick={() => setDraft({ ...draft, brand: v })} style={qButtonStyle(draft.brand === v)}>{lbl}</button>
                ))}
              </div>
            </div>
          ) : null}

          {/* Uploaded by — set/change who uploaded this file (saves immediately) */}
          {editing ? (
            <div>
              <Label>Uploaded by</Label>
              <select
                value={item.uploaded_by_admin_id || ''}
                onChange={(e) => onSave({ uploaded_by_admin_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                style={selectStyle()}
              >
                <option value="">{item.uploaded_by_name ? '(unassign)' : '(not set — assign)'}</option>
                {users.map((u) => <option key={u.admin_id} value={u.admin_id}>{u.staff_name}</option>)}
              </select>
            </div>
          ) : null}

          {/* Suggested name */}
          {editing ? (
            <div>
              <Label>Name slug</Label>
              <input type="text" value={draft.suggested_name} onChange={(e) => setDraft({ ...draft, suggested_name: e.target.value })} style={selectStyle()} />
            </div>
          ) : null}

          {/* Alt text */}
          <div>
            <Label>Alt text</Label>
            {editing ? (
              <textarea value={draft.alt_text} onChange={(e) => setDraft({ ...draft, alt_text: e.target.value })} rows={3} style={{ ...selectStyle(), resize: 'vertical' }} />
            ) : (
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>{item.alt_text}</div>
            )}
          </div>

          {/* Usable for */}
          <div>
            <Label>Usable for</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {USABLE_FOR.map((u) => {
                const on = (editing ? draft.usable_for : (item.usable_for || [])).includes(u);
                return (
                  <button key={u} disabled={!editing}
                    onClick={() => setDraft({ ...draft, usable_for: on ? draft.usable_for.filter((x) => x !== u) : [...draft.usable_for, u] })}
                    style={{ background: on ? '#d4eaff' : '#fff', color: on ? '#003a82' : 'var(--gray-500)', padding: '3px 9px', borderRadius: 3, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', border: `1px solid ${on ? '#a5cdf2' : 'var(--gray-200)'}`, cursor: editing ? 'pointer' : 'default' }}>
                    {u}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tags */}
          <div>
            <Label>Tags</Label>
            {editing ? (
              <>
                <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                  <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    placeholder="add a tag and press Enter" style={{ ...selectStyle(), flex: '1 1 auto', minWidth: 0 }} />
                  <button type="button" onClick={addTag} style={{ ...selectStyle(), width: 'auto', flex: '0 0 auto', padding: '6px 12px', background: 'var(--orange)', color: '#fff', cursor: 'pointer', borderColor: 'var(--orange)' }}><Plus size={14} /></button>
                </div>
                <div style={{ marginBottom: 6 }}>
                  <PresetChips presets={presets} onManage={onManagePresets}
                    onPick={(p) => setDraft({ ...draft, tags: [...draft.tags, ...(p.tags || []).filter((t) => !draft.tags.includes(t))] })} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {draft.tags.map((t) => (
                    <span key={t} style={{ background: 'var(--gray-100, #f0f0f3)', padding: '2px 4px 2px 7px', borderRadius: 3, fontSize: 11, color: '#424245', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      {t}
                      <button onClick={() => setDraft({ ...draft, tags: draft.tags.filter((x) => x !== t) })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1, display: 'inline-flex' }}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {(item.tags || []).map((t) => <span key={t} style={{ background: 'var(--gray-100, #f0f0f3)', padding: '2px 7px', borderRadius: 3, fontSize: 11, color: '#424245' }}>{t}</span>)}
              </div>
            )}
          </div>

          {/* Notes */}
          {(editing || item.notes) ? (
            <div>
              <Label>Notes</Label>
              {editing ? (
                <textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} rows={2} style={{ ...selectStyle(), resize: 'vertical' }} />
              ) : (
                <div style={{ fontSize: 12, background: '#fffbe6', padding: 8, borderRadius: 4, color: '#6b5800' }}>{item.notes}</div>
              )}
            </div>
          ) : null}

          {/* Save / Cancel.
              ⚠️ On a phone this is sticky. Unstuck it sits below alt text,
              usable-for, tags and notes, so labelling a photo ended in a scroll
              hunt for the only button that commits the edit. */}
          {editing ? (
            <div style={{
              display: 'flex', gap: 8, marginTop: 8,
              // Sticky on EVERY size, not just mobile. Rebecca, 2026-09-09:
              // "Can I edit the title in the media library. It appears that I
              // can't." She could - the Name slug box is a normal input - but on
              // a laptop the panel scrolls past Alt text, Usable for, Tags and
              // Notes, so Save changes sat below the fold and the edit never
              // committed. A field you can type in but not save reads as a
              // field you cannot edit.
              position: 'sticky', bottom: 0, zIndex: 3,
              background: '#fff', padding: '10px 0 4px',
              marginLeft: -14, marginRight: -14, paddingLeft: 14, paddingRight: 14,
              borderTop: '1px solid var(--gray-200)',
              boxShadow: '0 -6px 14px rgba(0,0,0,0.06)',
            }}>
              <button onClick={handleSave} disabled={saving}
                style={{ flex: 1, padding: '8px 12px', background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Save size={14} /> {saving ? 'Saving…' : 'Save changes'}
              </button>
              <button onClick={() => setEditing(false)} style={{ padding: '8px 14px', background: '#fff', color: 'var(--gray-500)', border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
            </div>
          ) : null}

          {item.r2_original_key ? (
            <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--gray-200)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Public URL</div>
              {[
                { kind: 'original', label: 'Original', url: publicUrl(item.r2_original_key) },
                ...(item.r2_thumb_key ? [{ kind: 'thumb', label: 'Thumb', url: publicUrl(item.r2_thumb_key) }] : []),
              ].map(({ kind, label, url }) => (
                <div key={kind} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '2px 0' }}>
                  <span style={{ flex: '0 0 50px', fontSize: 11, fontWeight: 600, color: 'var(--gray-500)' }}>{label}</span>
                  <input
                    type="text"
                    readOnly
                    value={url}
                    onFocus={(e) => e.target.select()}
                    style={{ flex: 1, fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', padding: '4px 6px', border: '1px solid var(--gray-200)', borderRadius: 3, background: '#fff', color: 'var(--gray-700)', minWidth: 0 }}
                  />
                  <button
                    type="button"
                    onClick={() => copyUrl(kind, url)}
                    title={copiedKind === kind ? 'Copied!' : 'Copy URL'}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 11, fontWeight: 600, background: '#fff', color: copiedKind === kind ? '#080' : 'var(--gray-700)', border: '1px solid var(--gray-200)', borderRadius: 3, cursor: 'pointer' }}
                  >
                    {copiedKind === kind ? <Check size={12} /> : <Link2 size={12} />}
                    {copiedKind === kind ? 'Copied' : 'Copy'}
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          <div style={{ marginTop: item.r2_original_key ? 12 : 'auto', paddingTop: 12, borderTop: '1px solid var(--gray-200)', fontSize: 11, color: 'var(--gray-500)' }}>
            {/* The id is printed because it is how this asset is named everywhere
                else - a report, a handoff, a message saying which photo to use -
                and the search box accepts it. Without it on screen, searching by
                number is a feature nobody can find. */}
            <div><strong>ID:</strong> {item.id}</div>
            <div style={{ wordBreak: 'break-all' }}><strong>Path:</strong> {item.source_path || ''}</div>
            <div><strong>Size:</strong> {item.size_bytes ? `${(item.size_bytes / 1024).toFixed(0)} KB` : '?'} · {item.confidence}</div>
            <div><strong>Dimensions:</strong> {item.width && item.height ? `${item.width} × ${item.height} px` : '—'}</div>
            <div>
              <strong>Uploaded:</strong>{' '}
              {item.classified_at ? new Date(item.classified_at).toLocaleDateString() : '—'}
              {' · '}
              <strong>by</strong>{' '}
              {item.uploaded_by_name || <span style={{ color: 'var(--gray-400)' }}>legacy import (set it via Edit)</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Subcomponents ────────────────────────────────────────────────────────────
function FilterGroup({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--gray-500)', marginBottom: 6, fontWeight: 600 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{children}</div>
    </div>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '3px 9px', fontSize: 11, borderRadius: 12, cursor: 'pointer',
      background: active ? 'var(--orange)' : '#fff',
      color: active ? '#fff' : '#424245',
      border: `1px solid ${active ? 'var(--orange)' : 'var(--gray-200)'}`,
      whiteSpace: 'nowrap',
    }}>{label}</button>
  );
}

function qButtonStyle(active) {
  return {
    flex: 1, padding: '4px 6px', fontSize: 11, fontWeight: 600,
    background: active ? 'var(--orange)' : '#fff', color: active ? '#fff' : '#424245',
    border: `1px solid ${active ? 'var(--orange)' : 'var(--gray-200)'}`, borderRadius: 4, cursor: 'pointer',
  };
}

function selectStyle() {
  return {
    padding: '6px 8px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 4,
    background: '#fff', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', color: '#1d1d1f',
  };
}

function Label({ children }) {
  return <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-500)', marginBottom: 4, fontWeight: 600 }}>{children}</div>;
}

function IconBtn({ onClick, title, icon, active }) {
  return (
    <button onClick={onClick} title={title}
      style={{ background: active ? '#ffd60a' : '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
      {icon}
    </button>
  );
}

// ── Bulk action bar (appears in selection mode) ──────────────────────────────
function BulkActionBar({ presets = [], onManagePresets, count, busy, message, popover, setPopover, onClear, onDone, tagOptions, tagsOnSelection, brandsOnSelection, onApplyAdd, onApplyRemove, onApplySetBrand, onApplySetUploader, onSend, users = [] }) {
  const disabled = count === 0 || busy;
  const isMobile = useIsMobile();
  return (
    <div style={{ position: 'fixed', left: isMobile ? 8 : '50%', right: isMobile ? 8 : 'auto', bottom: isMobile ? 'calc(16px + env(safe-area-inset-bottom))' : 48, transform: isMobile ? 'none' : 'translateX(-50%)', zIndex: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, pointerEvents: 'none' }}>
      {message ? (
        <div style={{ padding: '6px 14px', borderRadius: 18, fontSize: 12, fontWeight: 600, color: '#fff', background: message.kind === 'ok' ? '#1a7f37' : '#c33', boxShadow: '0 4px 14px rgba(0,0,0,0.25)', pointerEvents: 'auto' }}>
          {message.text}
        </div>
      ) : null}
      {popover === 'add' ? (
        <AddTagPopover busy={busy} options={tagOptions} onApply={onApplyAdd} onClose={() => setPopover(null)} />
      ) : null}
      {popover === 'preset' ? (
        <PresetPopover busy={busy} presets={presets} onApply={onApplyAdd} onManage={onManagePresets} onClose={() => setPopover(null)} />
      ) : null}
      {popover === 'remove' ? (
        <RemoveTagPopover busy={busy} tagsOnSelection={tagsOnSelection} onApply={onApplyRemove} onClose={() => setPopover(null)} />
      ) : null}
      {popover === 'brand' ? (
        <SetBrandPopover busy={busy} count={count} brandsOnSelection={brandsOnSelection} onApply={onApplySetBrand} onClose={() => setPopover(null)} />
      ) : null}
      {popover === 'uploader' ? (
        <SetUploaderPopover busy={busy} count={count} users={users} onApply={onApplySetUploader} onClose={() => setPopover(null)} />
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'space-between' : 'flex-start', gap: isMobile ? 4 : 6, padding: isMobile ? '6px 8px' : '8px 12px', background: 'var(--navy)', color: '#fff', borderRadius: 28, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', pointerEvents: 'auto', width: isMobile ? '100%' : 'auto', boxSizing: 'border-box' }}>
        <div style={{ fontSize: isMobile ? 11 : 13, fontWeight: 600, padding: isMobile ? '0 4px' : '0 10px 0 6px', whiteSpace: 'nowrap' }}>
          {count === 0 ? (isMobile ? '0' : 'Select images') : (isMobile ? `${count}` : `${count} selected`)}
        </div>
        <BarBtn icon={<Send size={13} />} label="Send" iconOnly={isMobile} onClick={onSend} disabled={disabled} />
        <BarBtn icon={<Tag size={13} />} label="Add tag" iconOnly={isMobile} onClick={() => setPopover(popover === 'add' ? null : 'add')} disabled={disabled} active={popover === 'add'} />
        <BarBtn icon={<Layers size={13} />} label="Preset" iconOnly={isMobile} onClick={() => setPopover(popover === 'preset' ? null : 'preset')} disabled={disabled} active={popover === 'preset'} />
        <BarBtn icon={<Minus size={13} />} label="Remove tag" iconOnly={isMobile} onClick={() => setPopover(popover === 'remove' ? null : 'remove')} disabled={disabled || tagsOnSelection.length === 0} active={popover === 'remove'} />
        <BarBtn icon={<Edit2 size={13} />} label="Set brand" iconOnly={isMobile} onClick={() => setPopover(popover === 'brand' ? null : 'brand')} disabled={disabled} active={popover === 'brand'} />
        <BarBtn icon={<UserRound size={13} />} label="Set uploader" iconOnly={isMobile} onClick={() => setPopover(popover === 'uploader' ? null : 'uploader')} disabled={disabled} active={popover === 'uploader'} />
        <BarBtn icon={<X size={13} />} label="Clear" iconOnly={isMobile} onClick={onClear} disabled={count === 0 || busy} />
        <button onClick={onDone} disabled={busy} style={{ marginLeft: 4, padding: isMobile ? '6px 10px' : '6px 14px', fontSize: 12, fontWeight: 700, background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 20, cursor: busy ? 'wait' : 'pointer' }}>
          Done
        </button>
      </div>
    </div>
  );
}

function BarBtn({ icon, label, onClick, disabled, active, iconOnly }) {
  return (
    <button onClick={onClick} disabled={disabled} title={iconOnly ? label : undefined} aria-label={label}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: iconOnly ? 0 : 5,
        padding: iconOnly ? 7 : '6px 11px', fontSize: 12, fontWeight: 600,
        background: active ? 'rgba(255,255,255,0.18)' : 'transparent',
        color: '#fff',
        border: '1px solid rgba(255,255,255,0.25)', borderRadius: 18,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
      }}>
      {icon}{iconOnly ? null : label}
    </button>
  );
}

function AddTagPopover({ busy, options, onApply, onClose }) {
  const [text, setText] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const normalize = (t) => t.trim().toLowerCase().replace(/\s+/g, '-');
  const apply = () => {
    const tags = text.split(',').map(normalize).filter(Boolean);
    if (!tags.length) return;
    onApply(tags);
    setText('');
  };
  const filtered = useMemo(() => {
    const q = normalize(text);
    if (!q) return [];
    return (options || [])
      .filter((o) => o.tag.includes(q) && o.tag !== q)
      .slice(0, 8);
  }, [text, options]);
  return (
    <div style={popoverStyle}>
      <div style={popoverHeaderStyle}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Add tag</div>
        <button onClick={onClose} style={popoverCloseStyle}><X size={12} /></button>
      </div>
      <div style={{ padding: 10 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <input ref={inputRef} type="text" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
            placeholder="motown or motown, event, c-and-c"
            disabled={busy}
            style={{ flex: 1, padding: '7px 10px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 4, fontFamily: 'inherit' }} />
          <button onClick={apply} disabled={busy || !text.trim()}
            style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 4, cursor: busy ? 'wait' : 'pointer', opacity: !text.trim() ? 0.5 : 1 }}>
            {busy ? '…' : 'Apply'}
          </button>
        </div>
        {filtered.length ? (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {filtered.map((o) => (
              <button key={o.tag} onClick={() => onApply([o.tag])} disabled={busy}
                style={{ padding: '3px 9px', fontSize: 11, background: '#fff', color: '#424245', border: '1px solid var(--gray-200)', borderRadius: 12, cursor: busy ? 'wait' : 'pointer' }}>
                {o.tag} <span style={{ color: 'var(--gray-500)' }}>({o.count})</span>
              </button>
            ))}
          </div>
        ) : null}
        <div style={{ marginTop: 8, fontSize: 10, color: 'var(--gray-500)' }}>
          Comma-separate to apply multiple. Existing matching tags appear as one-click chips.
        </div>
      </div>
    </div>
  );
}

function RemoveTagPopover({ busy, tagsOnSelection, onApply, onClose }) {
  return (
    <div style={popoverStyle}>
      <div style={popoverHeaderStyle}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Remove tag</div>
        <button onClick={onClose} style={popoverCloseStyle}><X size={12} /></button>
      </div>
      <div style={{ padding: 10 }}>
        {tagsOnSelection.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No tags on selected images.</div>
        ) : (
          <>
            <div style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 6 }}>
              Click a tag to remove it from all selected. Number = how many selected images carry it.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 200, overflowY: 'auto' }}>
              {tagsOnSelection.map(({ tag, count }) => (
                <button key={tag} onClick={() => onApply([tag])} disabled={busy}
                  style={{ padding: '4px 9px', fontSize: 11, background: '#fff', color: '#c33', border: '1px solid #ffd6d6', borderRadius: 12, cursor: busy ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Minus size={10} /> {tag} <span style={{ color: 'var(--gray-500)' }}>({count})</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SetUploaderPopover({ busy, count, users = [], onApply, onClose }) {
  const [uid, setUid] = useState('');
  const [onlyBlank, setOnlyBlank] = useState(true);
  return (
    <div style={popoverStyle}>
      <div style={popoverHeaderStyle}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Set uploaded by</div>
        <button onClick={onClose} style={popoverCloseStyle}><X size={12} /></button>
      </div>
      <div style={{ padding: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 8 }}>{count} image{count === 1 ? '' : 's'} selected.</div>
        <select value={uid} onChange={(e) => setUid(e.target.value)} style={{ width: '100%', fontSize: 12, padding: '7px 8px', border: '1px solid var(--gray-200)', borderRadius: 4, marginBottom: 8, fontFamily: 'Montserrat', color: 'var(--navy)', background: '#fff' }}>
          <option value="">Choose a person…</option>
          {users.map((u) => <option key={u.admin_id} value={u.admin_id}>{u.staff_name}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--navy)', cursor: 'pointer', marginBottom: 10 }}>
          <input type="checkbox" checked={onlyBlank} onChange={(e) => setOnlyBlank(e.target.checked)} /> Only the ones with no uploader yet
        </label>
        <button onClick={() => uid && onApply(parseInt(uid, 10), onlyBlank)} disabled={busy || !uid}
          style={{ width: '100%', padding: '8px 12px', fontSize: 12, fontWeight: 600, background: (busy || !uid) ? '#fafafa' : 'var(--orange)', color: (busy || !uid) ? 'var(--gray-500)' : '#fff', border: '1px solid', borderColor: (busy || !uid) ? 'var(--gray-200)' : 'var(--orange)', borderRadius: 4, cursor: (busy || !uid) ? 'not-allowed' : 'pointer' }}>
          {busy ? 'Applying…' : 'Apply'}
        </button>
      </div>
    </div>
  );
}

function SetBrandPopover({ busy, count, brandsOnSelection, onApply, onClose }) {
  const other = brandsOnSelection?.other || 0;
  const options = BRANDS.map((b) => ({ value: b.value, label: b.label, already: brandsOnSelection?.[b.value] || 0 }));
  const already = options.filter((o) => o.already).map((o) => `${o.already} ${brandShort(o.value)}`);
  if (other) already.push(`${other} other`);
  return (
    <div style={popoverStyle}>
      <div style={popoverHeaderStyle}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Set brand</div>
        <button onClick={onClose} style={popoverCloseStyle}><X size={12} /></button>
      </div>
      <div style={{ padding: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 8 }}>
          {count} image{count === 1 ? '' : 's'} selected{already.length ? ` · already ${already.join(' / ')}` : ''}.
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {options.map((o) => {
            const allMatch = o.already === count;
            return (
              <button key={o.value} onClick={() => onApply(o.value)} disabled={busy || allMatch}
                style={{
                  flex: 1, padding: '8px 12px', fontSize: 12, fontWeight: 600,
                  background: allMatch ? '#fafafa' : 'var(--orange)',
                  color: allMatch ? 'var(--gray-500)' : '#fff',
                  border: '1px solid', borderColor: allMatch ? 'var(--gray-200)' : 'var(--orange)',
                  borderRadius: 4, cursor: (busy || allMatch) ? 'not-allowed' : 'pointer',
                }}>
                {busy ? '…' : o.label}
                <div style={{ fontSize: 10, fontWeight: 500, opacity: 0.85, marginTop: 2 }}>
                  {allMatch ? 'all already this' : o.already > 0 ? `${o.already} already · ${count - o.already} will change` : `${count} will change`}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const popoverStyle = {
  background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10,
  width: 360, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
  pointerEvents: 'auto',
};
const popoverHeaderStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '8px 12px', borderBottom: '1px solid var(--gray-200)', background: '#fafafa',
};
const popoverCloseStyle = {
  background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--gray-500)', display: 'flex', alignItems: 'center',
};

// ── TAG PRESETS (Jessica, 2026-10-20, Team board "Tags"): a named group of tags applied in one
// click - one SEO preset per product or category, plus groups like People, Processing, UGC.
// Stored in Neon media_tag_presets; applying one just adds its tags, the same as typing them.
function PresetChips({ presets = [], onPick, onManage, disabled }) {
  if (!presets.length && !onManage) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginTop: 6 }}>
      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-500)', marginRight: 2 }}>Presets</span>
      {presets.map((p) => (
        <button key={p.id} type="button" disabled={disabled} onClick={() => onPick(p)} title={'Adds: ' + (p.tags || []).join(', ')}
          style={{ padding: '3px 9px', fontSize: 11, fontWeight: 600, background: '#eef4f8', color: 'var(--navy)', border: '1px solid #c6d6e2', borderRadius: 12, cursor: disabled ? 'wait' : 'pointer' }}>
          + {p.name} <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>({(p.tags || []).length})</span>
        </button>
      ))}
      {onManage ? (
        <button type="button" onClick={onManage} style={{ padding: '3px 6px', fontSize: 11, background: 'none', color: 'var(--orange)', border: 'none', textDecoration: 'underline', cursor: 'pointer' }}>
          {presets.length ? 'Edit presets' : 'Make a tag preset'}
        </button>
      ) : null}
    </div>
  );
}

function PresetPopover({ busy, presets, onApply, onManage, onClose }) {
  return (
    <div style={{ ...popoverStyle, width: 400, maxWidth: 'calc(100vw - 16px)' }}>
      <div style={popoverHeaderStyle}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Add a tag preset</div>
        <button onClick={onClose} style={popoverCloseStyle}><X size={12} /></button>
      </div>
      <div style={{ padding: 10, maxHeight: 340, overflowY: 'auto', display: 'grid', gap: 6 }}>
        {presets.length ? presets.map((p) => (
          <button key={p.id} onClick={() => onApply(p.tags || [])} disabled={busy}
            style={{ textAlign: 'left', padding: '8px 10px', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, cursor: busy ? 'wait' : 'pointer' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{p.name} <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: 11 }}>{(p.tags || []).length} tags</span></div>
            <div style={{ fontSize: 11, color: '#424245', marginTop: 2, lineHeight: 1.45 }}>{(p.tags || []).join(', ')}</div>
          </button>
        )) : <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No presets yet.</div>}
      </div>
      <div style={{ padding: '8px 10px', borderTop: '1px solid var(--gray-200)' }}>
        <button onClick={onManage} style={{ fontSize: 12, fontWeight: 600, background: 'none', border: 'none', color: 'var(--orange)', cursor: 'pointer', padding: 0 }}>Make or edit presets</button>
      </div>
    </div>
  );
}

function TagPresetsManager({ presets, adminId, onChanged, onClose }) {
  const blank = { id: null, name: '', tags: '' };
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await neonQuery('media_tag_presets_upsert', { id: edit.id, name: edit.name, tags: edit.tags, admin_id: adminId });
      setEdit(null); await onChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete the "${p.name}" preset? Photos already tagged keep their tags.`)) return;
    setBusy(true); setErr(null);
    try { await neonQuery('media_tag_presets_delete', { id: p.id }); await onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const field = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 6, fontFamily: 'inherit' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 'min(620px, 100%)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--gray-200)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>Tag presets</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>A group of tags you add in one click. One per product or category is a good start.</div>
          </div>
          <button onClick={onClose} style={popoverCloseStyle} aria-label="Close"><X size={16} /></button>
        </div>
        <div style={{ padding: 16, overflowY: 'auto', display: 'grid', gap: 8 }}>
          {err ? <div style={{ fontSize: 12, color: '#c33' }}>{err}</div> : null}
          {edit ? (
            <div style={{ border: '1px solid #c6d6e2', background: '#f7fafc', borderRadius: 8, padding: 12, display: 'grid', gap: 8 }}>
              <input autoFocus value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Preset name, e.g. Shrimp" style={field} />
              <textarea value={edit.tags} onChange={(e) => setEdit({ ...edit, tags: e.target.value })} rows={4}
                placeholder="Tags, separated by commas, e.g. Wild-caught, Sustainable, Key West" style={{ ...field, resize: 'vertical' }} />
              <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>Separate tags with commas. They are saved like every other tag: lowercase, spaces become hyphens.</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={save} disabled={busy || !edit.name.trim() || !edit.tags.trim()} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, cursor: busy ? 'wait' : 'pointer' }}>{busy ? 'Saving...' : 'Save preset'}</button>
                <button onClick={() => setEdit(null)} disabled={busy} style={{ padding: '8px 14px', fontSize: 13, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setEdit(blank)} style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 14px', fontSize: 13, fontWeight: 700, background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}><Plus size={14} /> New preset</button>
          )}
          {presets.map((p) => (
            <div key={p.id} style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>{p.name}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                  {(p.tags || []).map((t) => <span key={t} style={{ background: 'var(--gray-100, #f0f0f3)', padding: '2px 7px', borderRadius: 3, fontSize: 11, color: '#424245' }}>{t}</span>)}
                </div>
              </div>
              <button onClick={() => setEdit({ id: p.id, name: p.name, tags: (p.tags || []).join(', ') })} disabled={busy} title="Edit" aria-label={'Edit ' + p.name} style={{ ...popoverCloseStyle, color: 'var(--navy)' }}><Edit2 size={14} /></button>
              <button onClick={() => remove(p)} disabled={busy} title="Delete" aria-label={'Delete ' + p.name} style={popoverCloseStyle}><X size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
